import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import ninaAvatar from '../../assets/avatars/nina-avatar.svg';
import krytenAvatar from '../../assets/avatars/kryten-avatar.svg';
import polyAvatar from '../../assets/avatars/poly-avatar.svg';
import gordonAvatar from '../../assets/avatars/gordon-avatar.svg';
import reneeAvatar from '../../assets/avatars/renee-avatar.svg';
import chrisAvatar from '../../assets/avatars/chris-avatar.svg';
import { getUserRoleFromToken } from '../../utils/jwt';

function toSlugSegment(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function normalizeCountryCode(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, '');
}

function withPartnerId(url, partnerId = 'BEWYOUX') {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    if (!parsed.searchParams.get('partner_id')) {
      parsed.searchParams.set('partner_id', partnerId);
    }
    return parsed.toString();
  } catch (err) {
    const separator = raw.includes('?') ? '&' : '?';
    if (raw.includes('partner_id=')) return raw;
    return `${raw}${separator}partner_id=${encodeURIComponent(partnerId)}`;
  }
}

function parseNeighboringDestinations(value) {
  if (!value) return [];
  const raw = Array.isArray(value) ? value : (() => {
    try {
      return JSON.parse(value);
    } catch (err) {
      return [];
    }
  })();
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => ({
      name: String(entry?.name || entry?.destinationName || '').trim(),
      locationId: String(entry?.locationId || entry?.gygLocationId || '').trim(),
    }))
    .filter((entry) => entry.name)
    .slice(0, 10);
}

function createEmptyNeighboringDestination() {
  return {
    name: '',
    locationId: '',
  };
}

function moveListItem(items, fromIndex, toIndex) {
  if (!Array.isArray(items)) return [];
  if (fromIndex === toIndex) return items;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

const TOUR_OVERRIDE_SORT_OPTIONS = Object.freeze([
  { value: 'reviews_desc', label: 'Most Reviews' },
  { value: 'included', label: 'Included' },
  { value: 'published', label: 'Published' },
  { value: 'rating_desc', label: 'Highest Rating' },
  { value: 'rank_asc', label: 'Best Rank' },
  { value: 'price_asc', label: 'Lowest Price' },
  { value: 'title_asc', label: 'Title A-Z' },
  { value: 'match_desc', label: 'Best Match' },
]);

const TOUR_OVERRIDE_LOCATION_STOPWORDS = new Set([
  'tour',
  'tours',
  'trip',
  'trips',
  'private',
  'transfer',
  'transfers',
  'car',
  'cars',
  'motorbike',
  'motorbikes',
  'rider',
  'riders',
  'easy',
  'guided',
  'lunch',
  'dinner',
  'breakfast',
  'activity',
  'activities',
  'ticket',
  'tickets',
  'class',
  'classes',
  'workshop',
  'workshops',
  'group',
  'pickup',
  'pick',
  'line',
  'skip',
  'sightseeing',
  'stop',
  'stops',
  'scenic',
  'day',
]);

function normalizeFilterText(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function normalizeCompactFilterText(value) {
  return normalizeFilterText(value).replace(/\s+/g, '');
}

function extractTourTitleLocationCandidates(title) {
  const rawTitle = String(title || '').trim();
  if (!rawTitle) return [];
  const matches = rawTitle.match(/\b(?:[A-Z]{2,}|[A-Z][a-z]+)(?:\s+(?:[A-Z]{2,}|[A-Z][a-z]+)){0,2}\b/g) || [];
  return matches.filter((phrase) => {
    const normalizedWords = normalizeFilterText(phrase).split(' ').filter(Boolean);
    if (!normalizedWords.length || normalizedWords.length > 3) return false;
    if (normalizedWords.every((word) => TOUR_OVERRIDE_LOCATION_STOPWORDS.has(word))) return false;
    if (normalizedWords.some((word) => TOUR_OVERRIDE_LOCATION_STOPWORDS.has(word))) return false;
    return true;
  });
}

function buildTourOverrideLocationOptions({ tours = [], siteName = '', neighboringDestinations = [] }) {
  const options = new Map();
  const addOption = (label, weight = 1) => {
    const safeLabel = String(label || '').replace(/\s+/g, ' ').trim();
    if (!safeLabel) return;
    const normalizedWords = normalizeFilterText(safeLabel).split(' ').filter(Boolean);
    if (!normalizedWords.length) return;
    if (normalizedWords.every((word) => TOUR_OVERRIDE_LOCATION_STOPWORDS.has(word))) return;
    const value = normalizeCompactFilterText(safeLabel);
    if (!value) return;
    const existing = options.get(value) || { value, label: safeLabel, count: 0 };
    existing.count += weight;
    if (safeLabel.length < existing.label.length) existing.label = safeLabel;
    options.set(value, existing);
  };

  addOption(siteName, 4);
  neighboringDestinations.forEach((entry) => addOption(entry?.name, 3));
  tours.forEach((tour) => {
    extractTourTitleLocationCandidates(tour?.tourTitle).forEach((label) => addOption(label, 1));
  });

  return Array.from(options.values())
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label);
    })
    .slice(0, 20);
}

function sortTourOverrideRows(tours = [], sortBy = 'reviews_desc') {
  const list = Array.isArray(tours) ? [...tours] : [];
  list.sort((a, b) => {
    if (sortBy === 'rating_desc') {
      const diff = Number(b?.avgRating || 0) - Number(a?.avgRating || 0);
      if (diff !== 0) return diff;
    } else if (sortBy === 'rank_asc') {
      const aRank = Number(a?.partnerRank || 999999);
      const bRank = Number(b?.partnerRank || 999999);
      if (aRank !== bRank) return aRank - bRank;
    } else if (sortBy === 'price_asc') {
      const aPrice = Number(a?.priceFrom || 999999999);
      const bPrice = Number(b?.priceFrom || 999999999);
      if (aPrice !== bPrice) return aPrice - bPrice;
    } else if (sortBy === 'title_asc') {
      const titleDiff = String(a?.tourTitle || '').localeCompare(String(b?.tourTitle || ''));
      if (titleDiff !== 0) return titleDiff;
    } else if (sortBy === 'match_desc') {
      const diff = Number(b?.matchScore || 0) - Number(a?.matchScore || 0);
      if (diff !== 0) return diff;
    } else {
      const diff = Number(b?.reviewCount || 0) - Number(a?.reviewCount || 0);
      if (diff !== 0) return diff;
    }

    const reviewDiff = Number(b?.reviewCount || 0) - Number(a?.reviewCount || 0);
    if (reviewDiff !== 0) return reviewDiff;
    const ratingDiff = Number(b?.avgRating || 0) - Number(a?.avgRating || 0);
    if (ratingDiff !== 0) return ratingDiff;
    return String(a?.tourTitle || '').localeCompare(String(b?.tourTitle || ''));
  });
  return list;
}

function formatDatabaseColumnLabel(column) {
  return String(column || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getDatabaseCellText(value) {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

function compareDatabaseValues(a, b) {
  const aNum = Number(a);
  const bNum = Number(b);
  const aIsNum = Number.isFinite(aNum) && String(a).trim() !== '';
  const bIsNum = Number.isFinite(bNum) && String(b).trim() !== '';
  if (aIsNum && bIsNum) return aNum - bNum;
  return getDatabaseCellText(a).localeCompare(getDatabaseCellText(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function getDatabaseColumnUniqueValues(rows = [], column) {
  const values = new Map();
  for (const row of rows) {
    const text = getDatabaseCellText(row?.[column]).trim();
    if (!text) continue;
    values.set(text, (values.get(text) || 0) + 1);
  }
  return Array.from(values.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: 'base' });
    })
    .slice(0, 50);
}

const DEFAULT_AGENT_MODEL_OPTIONS = Object.freeze({
  openai: Object.freeze(['gpt-4o-mini']),
  gemini: Object.freeze(['gemini-2.5-flash']),
  anthropic: Object.freeze(['claude-sonnet-4-20250514']),
});

const DEFAULT_AGENT_IMAGE_MODEL_OPTIONS = Object.freeze({
  openai: Object.freeze(['gpt-image-1', 'dall-e-3', 'dall-e-2']),
  gemini: Object.freeze(['gemini-2.5-flash-image', 'gemini-2.0-flash-preview-image-generation']),
});

function normalizeAgentProvider(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'openai') return 'openai';
  if (normalized === 'anthropic') return 'anthropic';
  return 'gemini';
}

function normalizeAgentModelCatalog(data) {
  const providers = data?.providers || {};
  return {
    openai: Array.isArray(providers.openai) && providers.openai.length
      ? providers.openai.map((entry) => String(entry || '').trim()).filter(Boolean)
      : [...DEFAULT_AGENT_MODEL_OPTIONS.openai],
    gemini: Array.isArray(providers.gemini) && providers.gemini.length
      ? providers.gemini.map((entry) => String(entry || '').trim()).filter(Boolean)
      : [...DEFAULT_AGENT_MODEL_OPTIONS.gemini],
    anthropic: Array.isArray(providers.anthropic) && providers.anthropic.length
      ? providers.anthropic.map((entry) => String(entry || '').trim()).filter(Boolean)
      : [...DEFAULT_AGENT_MODEL_OPTIONS.anthropic],
  };
}

function normalizeAgentImageModelCatalog(data) {
  const providers = data?.imageProviders || {};
  return {
    openai: Array.isArray(providers.openai) && providers.openai.length
      ? providers.openai.map((entry) => String(entry || '').trim()).filter(Boolean)
      : [...DEFAULT_AGENT_IMAGE_MODEL_OPTIONS.openai],
    gemini: Array.isArray(providers.gemini) && providers.gemini.length
      ? providers.gemini.map((entry) => String(entry || '').trim()).filter(Boolean)
      : [...DEFAULT_AGENT_IMAGE_MODEL_OPTIONS.gemini],
  };
}

function normalizeAgentImageProvider(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'openai') return 'openai';
  return 'gemini';
}

function getAgentProviderModels(modelOptions, provider, currentModel = '') {
  const normalizedProvider = normalizeAgentProvider(provider);
  const options = Array.isArray(modelOptions?.[normalizedProvider]) ? modelOptions[normalizedProvider] : [];
  const fallbackOptions = options.length ? options : [...DEFAULT_AGENT_MODEL_OPTIONS[normalizedProvider]];
  const normalizedCurrentModel = String(currentModel || '').trim();
  if (normalizedCurrentModel && !fallbackOptions.includes(normalizedCurrentModel)) {
    return [normalizedCurrentModel, ...fallbackOptions];
  }
  return fallbackOptions;
}

function getAgentImageProviderModels(modelOptions, provider, currentModel = '') {
  const normalizedProvider = normalizeAgentImageProvider(provider);
  const options = Array.isArray(modelOptions?.[normalizedProvider]) ? modelOptions[normalizedProvider] : [];
  const fallbackOptions = options.length ? options : [...DEFAULT_AGENT_IMAGE_MODEL_OPTIONS[normalizedProvider]];
  const normalizedCurrentModel = String(currentModel || '').trim();
  if (normalizedCurrentModel && !fallbackOptions.includes(normalizedCurrentModel)) {
    return [normalizedCurrentModel, ...fallbackOptions];
  }
  return fallbackOptions;
}

function getAgentStatusClassName(status) {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'complete') return 'bg-green-700 text-white';
  if (normalized === 'error') return 'bg-red-700 text-white';
  if (normalized === 'generating') return 'bg-yellow-700 text-white';
  return 'bg-gray-600 text-white';
}

function formatAgentRunSummary({ provider, model, generatedAt, revisionId }) {
  const parts = [`${provider || 'n/a'}${model ? ` ${model}` : ''}`.trim()];
  if (revisionId) parts.push(`rev ${revisionId}`);
  if (generatedAt) parts.push(generatedAt);
  return parts.join(' · ');
}

const AGENT_CARD_BASE_CLASS =
  'relative overflow-hidden rounded-3xl border border-white/10 bg-slate-950/80 p-5 shadow-[0_18px_50px_rgba(15,23,42,0.35)]';
const DEFAULT_NINA_PROVIDER = 'gemini';
const DEFAULT_NINA_MODEL = 'gemini-2.5-flash';
const DEFAULT_KRYTEN_PROVIDER = 'anthropic';
const DEFAULT_KRYTEN_MODEL = 'claude-sonnet-4-20250514';
const DEFAULT_POLY_PROVIDER = 'openai';
const DEFAULT_POLY_MODEL = 'gpt-4o-mini';
const DEFAULT_GORDON_PROVIDER = 'openai';
const DEFAULT_GORDON_MODEL = 'gpt-4o';
const DEFAULT_GORDON_TEXT_PROVIDER = 'anthropic';
const DEFAULT_GORDON_TEXT_MODEL = 'claude-sonnet-4-20250514';
const DEFAULT_GORDON_PHOTO_PROVIDER = 'openai';
const DEFAULT_GORDON_PHOTO_MODEL = 'gpt-6-astra';
const DEFAULT_RENEE_PROVIDER = 'openai';
const DEFAULT_RENEE_MODEL = 'gpt-4o';
const DEFAULT_CHRIS_PROVIDER = 'gemini';
const DEFAULT_CHRIS_MODEL = 'gemini-2.5-flash-image';
const DEFAULT_WEBSTER_PROVIDER = 'anthropic';
const DEFAULT_WEBSTER_MODEL = 'claude-sonnet-4-20250514';

function buildDestinationAgentSettings(site) {
  return {
    ninaProvider: normalizeAgentProvider(site?.ninaProvider || site?.destination_nina_provider || DEFAULT_NINA_PROVIDER),
    ninaModel: String(site?.ninaModel || site?.destination_nina_model || DEFAULT_NINA_MODEL).trim() || DEFAULT_NINA_MODEL,
    krytenProvider: normalizeAgentProvider(site?.krytenProvider || site?.destination_kryten_provider || DEFAULT_KRYTEN_PROVIDER),
    krytenModel: String(site?.krytenModel || site?.destination_kryten_model || DEFAULT_KRYTEN_MODEL).trim() || DEFAULT_KRYTEN_MODEL,
    polyProvider: normalizeAgentProvider(site?.polyProvider || site?.destination_poly_provider || DEFAULT_POLY_PROVIDER),
    polyModel: String(site?.polyModel || site?.destination_poly_model || DEFAULT_POLY_MODEL).trim() || DEFAULT_POLY_MODEL,
    gordonProvider: normalizeAgentProvider(site?.gordonProvider || site?.destination_gordon_provider || DEFAULT_GORDON_PROVIDER),
    gordonModel: String(site?.gordonModel || site?.destination_gordon_model || DEFAULT_GORDON_MODEL).trim() || DEFAULT_GORDON_MODEL,
    gordonTextProvider: normalizeAgentProvider(
      site?.gordonTextProvider || site?.destination_gordon_text_provider || DEFAULT_GORDON_TEXT_PROVIDER
    ),
    gordonTextModel: String(
      site?.gordonTextModel || site?.destination_gordon_text_model || DEFAULT_GORDON_TEXT_MODEL
    ).trim() || DEFAULT_GORDON_TEXT_MODEL,
    gordonPhotoProvider: normalizeAgentProvider(
      site?.gordonPhotoProvider || site?.destination_gordon_photo_provider || DEFAULT_GORDON_PHOTO_PROVIDER
    ),
    gordonPhotoModel: String(
      site?.gordonPhotoModel || site?.destination_gordon_photo_model || DEFAULT_GORDON_PHOTO_MODEL
    ).trim() || DEFAULT_GORDON_PHOTO_MODEL,
    reneeProvider: normalizeAgentProvider(site?.reneeProvider || site?.destination_renee_provider || DEFAULT_RENEE_PROVIDER),
    reneeModel: String(site?.reneeModel || site?.destination_renee_model || DEFAULT_RENEE_MODEL).trim() || DEFAULT_RENEE_MODEL,
    chrisProvider: normalizeAgentImageProvider(site?.chrisProvider || site?.destination_chris_provider || DEFAULT_CHRIS_PROVIDER),
    chrisModel: String(site?.chrisModel || site?.destination_chris_model || DEFAULT_CHRIS_MODEL).trim() || DEFAULT_CHRIS_MODEL,
    websterProvider: normalizeAgentProvider(site?.websterProvider || site?.destination_webster_provider || DEFAULT_WEBSTER_PROVIDER),
    websterModel: String(site?.websterModel || site?.destination_webster_model || DEFAULT_WEBSTER_MODEL).trim() || DEFAULT_WEBSTER_MODEL,
  };
}

function extractPublishedHubTourTitles(hub) {
  const publishedFeaturedTours = Array.isArray(hub?.publishedContent?.featured_tours)
    ? hub.publishedContent.featured_tours
    : [];
  const fallbackTours = Array.isArray(hub?.tours) ? hub.tours : [];
  const titles = [...publishedFeaturedTours, ...fallbackTours]
    .map((entry) =>
      String(
        entry?.title ||
          entry?.name ||
          entry?.tourTitle ||
          entry?.tour_title ||
          entry?.headline ||
          ''
      ).trim()
    )
    .filter(Boolean);
  return Array.from(new Set(titles));
}

function getHubToursSelectedForNina(hub, minimumManualTarget = 5) {
  const selectedIdsFromApi = Array.isArray(hub?.ninaSelectedTourIds)
    ? hub.ninaSelectedTourIds.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  if (selectedIdsFromApi.length) return selectedIdsFromApi;

  const overrideByTourId = hub?.overrideByTourId && typeof hub.overrideByTourId === 'object'
    ? hub.overrideByTourId
    : {};
  const includedIds = Object.entries(overrideByTourId)
    .filter(([, mode]) => String(mode || '').trim().toLowerCase() === 'include')
    .map(([tourId]) => String(tourId || '').trim())
    .filter(Boolean);
  const otherActivityIds = new Set(
    Object.entries(overrideByTourId)
      .filter(([, mode]) => String(mode || '').trim().toLowerCase() === 'other_activities')
      .map(([tourId]) => String(tourId || '').trim())
      .filter(Boolean)
  );

  const autoBackupNeeded = Math.max(0, minimumManualTarget - (includedIds.length + otherActivityIds.size));
  const selected = [...includedIds];
  const seen = new Set(selected);
  const autoCandidates = Array.isArray(hub?.tours) ? hub.tours : [];
  for (const tour of autoCandidates) {
    if (selected.length >= includedIds.length + autoBackupNeeded) break;
    const tourId = String(tour?.tourId || tour?.tour_id || '').trim();
    if (!tourId || seen.has(tourId) || otherActivityIds.has(tourId)) continue;
    selected.push(tourId);
    seen.add(tourId);
  }
  return selected;
}

function getHubToursManuallyIncluded(hub) {
  const overrideByTourId = hub?.overrideByTourId && typeof hub.overrideByTourId === 'object'
    ? hub.overrideByTourId
    : {};
  return Object.entries(overrideByTourId)
    .filter(([, mode]) => {
      const normalized = String(mode || '').trim().toLowerCase();
      return normalized === 'include' || normalized === 'other_activities';
    })
    .map(([tourId]) => String(tourId || '').trim())
    .filter(Boolean);
}

export default function DestinationSetup({
  site,
  refreshSitesAreasDevices,
  onUpdateSiteFields = null,
  initialSetupTab = 'Destination Setup',
  hideSetupTabs = false,
}) {
  const history = useHistory();
  const isAdmin = getUserRoleFromToken(localStorage.getItem('token')) === 'admin';
  const normalizedInitialSetupTab = initialSetupTab === 'AI Agents' ? 'AI Agents' : 'Destination Setup';
  const setupTabs = useMemo(
    () => (
      hideSetupTabs
        ? [normalizedInitialSetupTab]
        : [
            'Destination Setup',
            'CSV Import & Classifier',
            'Tour Info',
            'Classifier Threshold & Scoring',
            'Activity Hubs (Manual Controls)',
          ]
    ),
    [hideSetupTabs, normalizedInitialSetupTab]
  );
  const [activeSetupTab, setActiveSetupTab] = useState(normalizedInitialSetupTab);
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [destinationSlug, setDestinationSlug] = useState('');
  const [siteSlug, setSiteSlug] = useState('');
  const [hubSlug, setHubSlug] = useState('');
  const [hubStatus, setHubStatus] = useState('unclassified');
  const [thresholdOverride, setThresholdOverride] = useState('');
  const [slugsLocked, setSlugsLocked] = useState(false);
  const [neighboringDestinations, setNeighboringDestinations] = useState([]);
  const [activityPageCityWidgetCount, setActivityPageCityWidgetCount] = useState('3');
  const [gygLocationId, setGygLocationId] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [globalThreshold, setGlobalThreshold] = useState(6);
  const [globalThresholdDraft, setGlobalThresholdDraft] = useState('6');
  const [hubTaxonomy, setHubTaxonomy] = useState([]);
  const [suggestedSlugDrafts, setSuggestedSlugDrafts] = useState({});
  const [dismissedSuggestedSlugs, setDismissedSuggestedSlugs] = useState([]);
  const [suggestionActions, setSuggestionActions] = useState({});
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalSaving, setGlobalSaving] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [globalMessage, setGlobalMessage] = useState('');

  const [snapshotStatus, setSnapshotStatus] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState('');
  const [tourInfoRows, setTourInfoRows] = useState([]);
  const [tourInfoColumns, setTourInfoColumns] = useState([]);
  const [tourInfoSnapshot, setTourInfoSnapshot] = useState(null);
  const [tourInfoLoading, setTourInfoLoading] = useState(false);
  const [tourInfoError, setTourInfoError] = useState('');
  const [tourInfoSearch, setTourInfoSearch] = useState('');
  const [tourInfoFilters, setTourInfoFilters] = useState({});
  const [tourInfoSort, setTourInfoSort] = useState({ column: 'review_count', direction: 'desc' });
  const [tourInfoSelectedRows, setTourInfoSelectedRows] = useState({});
  const [tourInfoMoveTarget, setTourInfoMoveTarget] = useState('');
  const [tourInfoMoving, setTourInfoMoving] = useState(false);
  const [keywordsFile, setKeywordsFile] = useState(null);
  const [activitiesFile, setActivitiesFile] = useState(null);
  const [newActivitiesFile, setNewActivitiesFile] = useState(null);
  const [dealsFile, setDealsFile] = useState(null);
  const [htmlFiles, setHtmlFiles] = useState([]);
  const [uploadingKeywords, setUploadingKeywords] = useState(false);
  const [uploadingActivities, setUploadingActivities] = useState(false);
  const [uploadingNewActivities, setUploadingNewActivities] = useState(false);
  const [uploadingDeals, setUploadingDeals] = useState(false);
  const [uploadingHtml, setUploadingHtml] = useState(false);
  const [classifierRunning, setClassifierRunning] = useState(false);
  const [classifierMessage, setClassifierMessage] = useState('');
  const [classifierError, setClassifierError] = useState('');
  const [classifierPreview, setClassifierPreview] = useState(null);
  const [classifierPreviewLoading, setClassifierPreviewLoading] = useState(false);
  const [classifierPreviewError, setClassifierPreviewError] = useState('');
  const [buildRunning, setBuildRunning] = useState(false);
  const [hubs, setHubs] = useState([]);
  const [hubsLoading, setHubsLoading] = useState(false);
  const [hubsError, setHubsError] = useState('');
  const [hubDrafts, setHubDrafts] = useState({});
  const [hubWidgetDrafts, setHubWidgetDrafts] = useState({});
  const [hubSavingId, setHubSavingId] = useState(null);
  const [hubToggleSavingId, setHubToggleSavingId] = useState(null);
  const [tourOverrideSavingKey, setTourOverrideSavingKey] = useState('');
  const [tourOverrideFilters, setTourOverrideFilters] = useState({});
  const [ninaProvider, setNinaProvider] = useState(DEFAULT_NINA_PROVIDER);
  const [ninaModel, setNinaModel] = useState(DEFAULT_NINA_MODEL);
  const [krytenProvider, setKrytenProvider] = useState(DEFAULT_KRYTEN_PROVIDER);
  const [krytenModel, setKrytenModel] = useState(DEFAULT_KRYTEN_MODEL);
  const [polyProvider, setPolyProvider] = useState(DEFAULT_POLY_PROVIDER);
  const [polyModel, setPolyModel] = useState(DEFAULT_POLY_MODEL);
  const [gordonProvider, setGordonProvider] = useState(DEFAULT_GORDON_PROVIDER);
  const [gordonModel, setGordonModel] = useState(DEFAULT_GORDON_MODEL);
  const [gordonTextProvider, setGordonTextProvider] = useState(DEFAULT_GORDON_TEXT_PROVIDER);
  const [gordonPhotoProvider, setGordonPhotoProvider] = useState(DEFAULT_GORDON_PHOTO_PROVIDER);
  const [gordonTextModel, setGordonTextModel] = useState(DEFAULT_GORDON_TEXT_MODEL);
  const [gordonPhotoModel, setGordonPhotoModel] = useState(DEFAULT_GORDON_PHOTO_MODEL);
  const [reneeProvider, setReneeProvider] = useState(DEFAULT_RENEE_PROVIDER);
  const [reneeModel, setReneeModel] = useState(DEFAULT_RENEE_MODEL);
  const [chrisProvider, setChrisProvider] = useState(DEFAULT_CHRIS_PROVIDER);
  const [chrisModel, setChrisModel] = useState(DEFAULT_CHRIS_MODEL);
  const [websterProvider, setWebsterProvider] = useState(DEFAULT_WEBSTER_PROVIDER);
  const [websterModel, setWebsterModel] = useState(DEFAULT_WEBSTER_MODEL);
  const [ninaModelOptions, setNinaModelOptions] = useState({
    openai: [...DEFAULT_AGENT_MODEL_OPTIONS.openai],
    gemini: [...DEFAULT_AGENT_MODEL_OPTIONS.gemini],
    anthropic: [...DEFAULT_AGENT_MODEL_OPTIONS.anthropic],
  });
  const [chrisModelOptions, setChrisModelOptions] = useState({
    openai: [...DEFAULT_AGENT_IMAGE_MODEL_OPTIONS.openai],
    gemini: [...DEFAULT_AGENT_IMAGE_MODEL_OPTIONS.gemini],
  });
  const [ninaModelsLoading, setNinaModelsLoading] = useState(false);
  const [ninaModelsError, setNinaModelsError] = useState('');
  const [ninaSendingAll, setNinaSendingAll] = useState(false);
  const [ninaSendingHubId, setNinaSendingHubId] = useState(null);
  const [ninaPublishingHubId, setNinaPublishingHubId] = useState(null);
  const [ninaMessage, setNinaMessage] = useState('');
  const [ninaError, setNinaError] = useState('');
  const [krytenHumanizingHubId, setKrytenHumanizingHubId] = useState(null);
  const [krytenMessage, setKrytenMessage] = useState('');
  const [krytenError, setKrytenError] = useState('');
  const [agentSettingsSaving, setAgentSettingsSaving] = useState(false);
  const [agentSettingsError, setAgentSettingsError] = useState('');
  const [agentSettingsMessage, setAgentSettingsMessage] = useState('');
  const [hubPanelExpanded, setHubPanelExpanded] = useState({});
  const [hubCardExpanded, setHubCardExpanded] = useState({});
  const lastSavedAgentSettingsRef = useRef(buildDestinationAgentSettings());

  useEffect(() => {
    setActiveSetupTab(normalizedInitialSetupTab);
    setName(site?.name || '');
    setCountryCode(site?.country_code || '');
    setDestinationSlug(site?.destination_slug || '');
    setSiteSlug(site?.site_slug || '');
    setHubSlug(site?.hub_slug || '');
    setHubStatus(site?.hub_status || 'unclassified');
    setThresholdOverride(
      site?.classifier_threshold_override == null ? '' : String(site.classifier_threshold_override)
    );
    setSlugsLocked(Boolean(site?.slugs_locked));
    setNeighboringDestinations(parseNeighboringDestinations(site?.neighboring_destinations_json));
    setActivityPageCityWidgetCount(
      site?.activity_page_city_widget_count == null ? '3' : String(site.activity_page_city_widget_count)
    );
    setGygLocationId(String(site?.gyg_location_id || '').trim());
    setMessage('');
    setError('');
    setSnapshotStatus(null);
    setSnapshotError('');
    setTourInfoRows([]);
    setTourInfoColumns([]);
    setTourInfoSnapshot(null);
    setTourInfoError('');
    setTourInfoSearch('');
    setTourInfoFilters({});
    setTourInfoSort({ column: 'review_count', direction: 'desc' });
    setTourInfoSelectedRows({});
    setTourInfoMoveTarget('');
    setTourInfoMoving(false);
    setKeywordsFile(null);
    setActivitiesFile(null);
    setNewActivitiesFile(null);
    setDealsFile(null);
    setHtmlFiles([]);
    setClassifierMessage('');
    setClassifierError('');
    setClassifierPreview(null);
    setClassifierPreviewError('');
    setHubs([]);
    setHubDrafts({});
    setHubWidgetDrafts({});
    setHubsError('');
    setTourOverrideFilters({});
    setNinaSendingAll(false);
    setNinaSendingHubId(null);
    setNinaPublishingHubId(null);
    setNinaMessage('');
    setNinaError('');
    setKrytenHumanizingHubId(null);
    setKrytenMessage('');
    setKrytenError('');
    setAgentSettingsSaving(false);
    setAgentSettingsError('');
    setAgentSettingsMessage('');
    setSuggestedSlugDrafts({});
    setDismissedSuggestedSlugs([]);
    setSuggestionActions({});
    setHubPanelExpanded({});
    setHubCardExpanded({});
  }, [
    normalizedInitialSetupTab,
    site?.id,
  ]);

  useEffect(() => {
    if (setupTabs.includes(activeSetupTab)) return;
    setActiveSetupTab(setupTabs[0] || normalizedInitialSetupTab);
  }, [activeSetupTab, normalizedInitialSetupTab, setupTabs]);

  useEffect(() => {
    if (!site?.id) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    let cancelled = false;

    const loadNinaModelCatalog = async () => {
      setNinaModelsLoading(true);
      setNinaModelsError('');
      try {
        const res = await fetch('/api/ai/models/catalog', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.error || 'Failed to load AI agent models.');
        }
        if (cancelled) return;
        setNinaModelOptions(normalizeAgentModelCatalog(data));
        setChrisModelOptions(normalizeAgentImageModelCatalog(data));
        const providerErrors = data?.errors || {};
        const nextError = [providerErrors.gemini, providerErrors.openai, providerErrors.anthropic].find(Boolean) || '';
        setNinaModelsError(nextError);
      } catch (err) {
        if (cancelled) return;
        setNinaModelOptions({
          openai: [...DEFAULT_AGENT_MODEL_OPTIONS.openai],
          gemini: [...DEFAULT_AGENT_MODEL_OPTIONS.gemini],
          anthropic: [...DEFAULT_AGENT_MODEL_OPTIONS.anthropic],
        });
        setChrisModelOptions({
          openai: [...DEFAULT_AGENT_IMAGE_MODEL_OPTIONS.openai],
          gemini: [...DEFAULT_AGENT_IMAGE_MODEL_OPTIONS.gemini],
        });
        setNinaModelsError(err.message || 'Failed to load AI agent models.');
      } finally {
        if (!cancelled) setNinaModelsLoading(false);
      }
    };

    loadNinaModelCatalog();

    return () => {
      cancelled = true;
    };
  }, [site?.id]);

  useEffect(() => {
    const providerModels = getAgentProviderModels(ninaModelOptions, ninaProvider, ninaModel);
    if (!providerModels.includes(ninaModel)) {
      setNinaModel(providerModels[0] || '');
    }
  }, [ninaModel, ninaModelOptions, ninaProvider]);

  useEffect(() => {
    const providerModels = getAgentProviderModels(ninaModelOptions, krytenProvider, krytenModel);
    if (!providerModels.includes(krytenModel)) {
      setKrytenModel(providerModels[0] || '');
    }
  }, [krytenModel, krytenProvider, ninaModelOptions]);

  useEffect(() => {
    const providerModels = getAgentProviderModels(ninaModelOptions, polyProvider, polyModel);
    if (!providerModels.includes(polyModel)) {
      setPolyModel(providerModels[0] || '');
    }
  }, [polyModel, polyProvider, ninaModelOptions]);

  useEffect(() => {
    const providerModels = getAgentProviderModels(ninaModelOptions, gordonProvider, gordonModel);
    if (!providerModels.includes(gordonModel)) {
      setGordonModel(providerModels[0] || '');
    }
  }, [gordonModel, gordonProvider, ninaModelOptions]);

  useEffect(() => {
    const providerModels = getAgentProviderModels(ninaModelOptions, gordonTextProvider, gordonTextModel);
    if (!providerModels.includes(gordonTextModel)) {
      setGordonTextModel(providerModels[0] || '');
    }
  }, [gordonTextModel, gordonTextProvider, ninaModelOptions]);

  useEffect(() => {
    const providerModels = getAgentProviderModels(ninaModelOptions, reneeProvider, reneeModel);
    if (!providerModels.includes(reneeModel)) {
      setReneeModel(providerModels[0] || '');
    }
  }, [ninaModelOptions, reneeModel, reneeProvider]);

  useEffect(() => {
    const providerModels = getAgentImageProviderModels(chrisModelOptions, chrisProvider, chrisModel);
    if (!providerModels.includes(chrisModel)) {
      setChrisModel(providerModels[0] || '');
    }
  }, [chrisModel, chrisModelOptions, chrisProvider]);

  useEffect(() => {
    const providerModels = getAgentProviderModels(ninaModelOptions, websterProvider, websterModel);
    if (!providerModels.includes(websterModel)) {
      setWebsterModel(providerModels[0] || '');
    }
  }, [ninaModelOptions, websterModel, websterProvider]);

  const loadSnapshotStatus = async () => {
    if (!site?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setSnapshotLoading(true);
    setSnapshotError('');
    try {
      const res = await fetch(`/api/sites/${site.id}/destination-snapshot-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error('Failed to load snapshot status.');
      }
      const data = await res.json().catch(() => ({}));
      setSnapshotStatus(data?.snapshot || null);
    } catch (err) {
      setSnapshotError(err.message || 'Failed to load snapshot status.');
    } finally {
      setSnapshotLoading(false);
    }
  };

  const loadTourInfo = async () => {
    if (!site?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setTourInfoLoading(true);
    setTourInfoError('');
    try {
      const res = await fetch(`/api/sites/${site.id}/destination-tours`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || 'Failed to load destination tours.');
      }
      setTourInfoSnapshot(data?.snapshot || null);
      setTourInfoColumns(Array.isArray(data?.columns) ? data.columns : []);
      setTourInfoRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      setTourInfoSnapshot(null);
      setTourInfoColumns([]);
      setTourInfoRows([]);
      setTourInfoError(err.message || 'Failed to load destination tours.');
    } finally {
      setTourInfoLoading(false);
    }
  };

  const loadHubs = async () => {
    if (!site?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setHubsLoading(true);
    setHubsError('');
    try {
      const res = await fetch(`/api/sites/${site.id}/activity-hubs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load activity hubs.');
      const data = await res.json().catch(() => ({}));
      const nextHubs = Array.isArray(data?.hubs) ? data.hubs : [];
      setHubs(nextHubs);
      setHubDrafts((prev) => {
        const next = { ...prev };
        for (const hub of nextHubs) {
          const key = String(hub.areaId);
          next[key] = {
            name: hub.name || '',
            slug: hub.slug || '',
          };
        }
        return next;
      });
      setHubWidgetDrafts((prev) => {
        const next = { ...prev };
        for (const hub of nextHubs) {
          const key = String(hub.areaId);
          const fallbackQuery = `${site?.name || ''} ${hub?.name || ''}`.trim();
          const fallbackCampaign = `SCV-${site?.id || ''}-${toSlugSegment(site?.destination_slug || destinationSlug || site?.name || '')}-${toSlugSegment(hub?.slug || '')}`.slice(0, 120);
          next[key] = {
            query: prev[key]?.query ?? fallbackQuery,
            localeCode: prev[key]?.localeCode ?? 'en-US',
            numberOfItems: prev[key]?.numberOfItems ?? '20',
            campaignId: prev[key]?.campaignId ?? fallbackCampaign,
            partnerId: prev[key]?.partnerId ?? 'BEWYOUX',
            updateExisting: prev[key]?.updateExisting ?? false,
          };
        }
        return next;
      });
    } catch (err) {
      setHubsError(err.message || 'Failed to load activity hubs.');
    } finally {
      setHubsLoading(false);
    }
  };

  const loadClassifierPreview = async () => {
    if (!site?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setClassifierPreviewLoading(true);
    setClassifierPreviewError('');
    try {
      const res = await fetch(`/api/sites/${site.id}/destination-json-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load classifier preview.');
      const data = await res.json().catch(() => ({}));
      setClassifierPreview(data?.destination || null);
    } catch (err) {
      setClassifierPreview(null);
      setClassifierPreviewError(err.message || 'Failed to load classifier preview.');
    } finally {
      setClassifierPreviewLoading(false);
    }
  };

  useEffect(() => {
    loadSnapshotStatus();
    loadTourInfo();
    loadClassifierPreview();
    loadHubs();
  }, [site?.id]);

  useEffect(() => {
    let active = true;
    const loadGlobal = async () => {
      if (!isAdmin) {
        setGlobalLoading(false);
        setGlobalError('');
        return;
      }
      const token = localStorage.getItem('token');
      if (!token) return;
      setGlobalLoading(true);
      setGlobalError('');
      try {
        const res = await fetch('/api/admin/platform-settings', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            throw new Error('Global threshold is admin-only. You can still set a per-destination override.');
          }
          throw new Error('Failed to load global threshold.');
        }
        const data = await res.json().catch(() => ({}));
        if (!active) return;
        const next = Number(data?.tourismClassifierMinActivities || 6);
        const nextAgentSettings = buildDestinationAgentSettings(data);
        setGlobalThreshold(next);
        setGlobalThresholdDraft(String(next));
        const taxonomy = Array.isArray(data?.tourismHubTaxonomy) ? data.tourismHubTaxonomy : [];
        setHubTaxonomy(
          taxonomy.map((item) => ({
            slug: String(item?.slug || '').trim(),
            name: String(item?.name || '').trim(),
            synonyms: Array.isArray(item?.synonyms) ? item.synonyms.join(', ') : '',
          }))
        );
        lastSavedAgentSettingsRef.current = nextAgentSettings;
        setNinaProvider(nextAgentSettings.ninaProvider);
        setNinaModel(nextAgentSettings.ninaModel);
        setKrytenProvider(nextAgentSettings.krytenProvider);
        setKrytenModel(nextAgentSettings.krytenModel);
        setPolyProvider(nextAgentSettings.polyProvider);
        setPolyModel(nextAgentSettings.polyModel);
        setGordonProvider(nextAgentSettings.gordonProvider);
        setGordonModel(nextAgentSettings.gordonModel);
        setGordonTextProvider(nextAgentSettings.gordonTextProvider);
        setGordonPhotoProvider(nextAgentSettings.gordonPhotoProvider);
        setGordonTextModel(nextAgentSettings.gordonTextModel);
        setGordonPhotoModel(nextAgentSettings.gordonPhotoModel);
        setReneeProvider(nextAgentSettings.reneeProvider);
        setReneeModel(nextAgentSettings.reneeModel);
        setChrisProvider(nextAgentSettings.chrisProvider);
        setChrisModel(nextAgentSettings.chrisModel);
        setWebsterProvider(nextAgentSettings.websterProvider);
        setWebsterModel(nextAgentSettings.websterModel);
        setAgentSettingsError('');
      } catch (err) {
        if (!active) return;
        setGlobalError(err.message || 'Failed to load global threshold.');
      } finally {
        if (active) setGlobalLoading(false);
      }
    };
    loadGlobal();
    return () => {
      active = false;
    };
  }, [isAdmin]);

  const canonicalPath = useMemo(() => {
    const cc = normalizeCountryCode(countryCode);
    const d = toSlugSegment(destinationSlug || name);
    const leaf = toSlugSegment(siteSlug || 'overview');
    if (!cc || !d || !leaf) return '';
    return `/explore/${cc}/${d}/${leaf}`;
  }, [countryCode, destinationSlug, name, siteSlug]);

  if (!site) {
    return <p>Select a destination site to configure destination setup.</p>;
  }

  const isDestination = String(site.site_type || '').toLowerCase() === 'destination';

  const saveSite = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSaving(true);
    setMessage('');
    setError('');

    try {
      if (!String(gygLocationId || '').trim()) {
        throw new Error('Destination GetYourGuide Location ID is required.');
      }
      const incompleteNeighbor = neighboringDestinations.find(
        (entry) => String(entry?.name || '').trim() && !String(entry?.locationId || '').trim()
      );
      if (incompleteNeighbor) {
        throw new Error(`GetYourGuide Location ID is required for neighbouring destination "${incompleteNeighbor.name}".`);
      }
      await axios.put(
        `/api/sites/${site.id}`,
        {
          name: String(name || '').trim(),
          siteType: 'destination',
          countryCode: normalizeCountryCode(countryCode),
          destinationSlug: destinationSlug || name,
          siteSlug: siteSlug || 'overview',
          hubSlug: hubSlug || null,
          hubStatus,
          classifierThresholdOverride: thresholdOverride === '' ? null : thresholdOverride,
          slugsLocked,
          neighboringDestinations: neighboringDestinations
            .map((entry) => ({
              name: String(entry?.name || '').trim(),
              locationId: String(entry?.locationId || '').trim(),
            }))
            .filter((entry) => entry.name)
            .slice(0, 10),
          activityPageCityWidgetCount: activityPageCityWidgetCount === ''
            ? 3
            : Math.max(0, Math.min(10, Number.parseInt(activityPageCityWidgetCount, 10) || 0)),
          gygLocationId: String(gygLocationId || '').trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (refreshSitesAreasDevices) {
        await refreshSitesAreasDevices(site.id, undefined, true);
      }
      setMessage('Destination settings saved.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save destination settings.');
    } finally {
      setSaving(false);
    }
  };

  const persistClassifierSettings = async ({ silent = false } = {}) => {
    if (!isAdmin) return false;
    const token = localStorage.getItem('token');
    if (!token) return false;
    if (!silent) {
      setGlobalSaving(true);
      setGlobalError('');
      setGlobalMessage('');
    }
    try {
      const value = Math.max(1, Number.parseInt(globalThresholdDraft, 10) || 1);
      const res = await fetch('/api/admin/platform-settings', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tourismClassifierMinActivities: value,
          tourismHubTaxonomy: hubTaxonomy.map((item) => ({
            slug: String(item?.slug || '').trim(),
            name: String(item?.name || '').trim(),
            synonyms: String(item?.synonyms || '')
              .split(',')
              .map((entry) => entry.trim())
              .filter(Boolean),
          })),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || 'Failed to save global threshold.');
      }
      const body = await res.json().catch(() => ({}));
      const nextThreshold = Number(body?.tourismClassifierMinActivities || value);
      const nextTaxonomy = Array.isArray(body?.tourismHubTaxonomy) ? body.tourismHubTaxonomy : null;
      setGlobalThreshold(nextThreshold);
      setGlobalThresholdDraft(String(nextThreshold));
      if (nextTaxonomy) {
        setHubTaxonomy(
          nextTaxonomy.map((item) => ({
            slug: String(item?.slug || '').trim(),
            name: String(item?.name || '').trim(),
            synonyms: Array.isArray(item?.synonyms) ? item.synonyms.join(', ') : '',
          }))
        );
      }
      if (!silent) {
        setGlobalMessage('Classifier settings saved.');
      }
      await loadClassifierPreview();
      await loadHubs();
      return true;
    } catch (err) {
      setGlobalError(err.message || 'Failed to save classifier settings.');
      return false;
    } finally {
      if (!silent) {
        setGlobalSaving(false);
      }
    }
  };

  const saveGlobalThreshold = async () => {
    await persistClassifierSettings({ silent: false });
  };

  const uploadCsv = async (csvType) => {
    const token = localStorage.getItem('token');
    if (!token || !site?.id) return;
    const file =
      csvType === 'keywords'
        ? keywordsFile
        : csvType === 'activities'
          ? activitiesFile
          : csvType === 'new_activities'
            ? newActivitiesFile
            : dealsFile;
    if (!file) {
      setClassifierError(`Please select a ${csvType} CSV file first.`);
      return;
    }

    if (csvType === 'keywords') setUploadingKeywords(true);
    if (csvType === 'activities') setUploadingActivities(true);
    if (csvType === 'new_activities') setUploadingNewActivities(true);
    if (csvType === 'deals') setUploadingDeals(true);
    setClassifierError('');
    setClassifierMessage('');
    try {
      const csvText = await file.text();
      const res = await fetch(`/api/sites/${site.id}/destination-snapshots`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ csvType, csvText }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Failed to upload ${csvType} CSV.`);
      }
      setClassifierMessage(data?.message || `${csvType} CSV uploaded.`);
      await loadSnapshotStatus();
      await loadTourInfo();
      await loadClassifierPreview();
    } catch (err) {
      setClassifierError(err.message || `Failed to upload ${csvType} CSV.`);
    } finally {
      if (csvType === 'keywords') setUploadingKeywords(false);
      if (csvType === 'activities') setUploadingActivities(false);
      if (csvType === 'new_activities') setUploadingNewActivities(false);
      if (csvType === 'deals') setUploadingDeals(false);
    }
  };

  const uploadHtml = async () => {
    const token = localStorage.getItem('token');
    if (!token || !site?.id) return;
    if (!htmlFiles.length) {
      setClassifierError('Please select one or more saved GetYourGuide HTML files first.');
      return;
    }

    setUploadingHtml(true);
    setClassifierError('');
    setClassifierMessage('');
    try {
      const payloadFiles = [];
      for (const file of htmlFiles) {
        const htmlText = await file.text();
        payloadFiles.push({
          filename: file.name,
          htmlText,
        });
      }
      const res = await fetch(`/api/sites/${site.id}/destination-snapshots`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          csvType: 'html',
          htmlFiles: payloadFiles,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || 'Failed to upload saved GetYourGuide HTML.');
      }
      setClassifierMessage(data?.message || 'Saved GetYourGuide HTML uploaded.');
      setHtmlFiles([]);
      await loadSnapshotStatus();
      await loadTourInfo();
      await loadClassifierPreview();
    } catch (err) {
      setClassifierError(err.message || 'Failed to upload saved GetYourGuide HTML.');
    } finally {
      setUploadingHtml(false);
    }
  };

  const runClassifier = async () => {
    const token = localStorage.getItem('token');
    if (!token || !site?.id) return;
    setClassifierRunning(true);
    setClassifierError('');
    setClassifierMessage('');
    try {
      const settingsSaved = await persistClassifierSettings({ silent: true });
      if (!settingsSaved) {
        throw new Error('Save your classifier settings first. The canonical hub changes were not persisted.');
      }
      const res = await fetch(`/api/sites/${site.id}/run-classifier`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || 'Classifier failed.');
      }
      setClassifierMessage(
        `Classifier complete. ${Number(data?.buildEligible || 0)} canonical hub(s) are ready to build, ${Number(
          data?.suggestedPatterns || 0
        )} new suggestion(s) need taxonomy review.`
      );
      if (refreshSitesAreasDevices) {
        await refreshSitesAreasDevices(site.id, undefined, true);
      }
      await loadSnapshotStatus();
      await loadTourInfo();
      await loadClassifierPreview();
      await loadHubs();
    } catch (err) {
      setClassifierError(err.message || 'Classifier failed.');
    } finally {
      setClassifierRunning(false);
    }
  };

  const buildActivityHubs = async () => {
    const token = localStorage.getItem('token');
    if (!token || !site?.id) return;
    setBuildRunning(true);
    setClassifierError('');
    setClassifierMessage('');
    try {
      const res = await fetch(`/api/sites/${site.id}/build-activity-hubs`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to build activity hubs.');
      setClassifierMessage(
        `Built ${Number(data?.totalBuilt || 0)} hub(s). Updated ${Number(
          data?.updatedInPlace || 0
        )}, created ${Number(data?.createdNew || 0)}.`
      );
      if (refreshSitesAreasDevices) {
        await refreshSitesAreasDevices(site.id, undefined, true);
      }
      await loadClassifierPreview();
      await loadHubs();
    } catch (err) {
      setClassifierError(err.message || 'Failed to build activity hubs.');
    } finally {
      setBuildRunning(false);
    }
  };

  const saveHub = async (hub) => {
    const token = localStorage.getItem('token');
    if (!token || !site?.id || !hub?.areaId) return;
    const key = String(hub.areaId);
    const draft = hubDrafts[key] || { name: hub.name, slug: hub.slug };
    setHubSavingId(hub.areaId);
    setHubsError('');
    try {
      const payload = {
        name: String(draft.name || '').trim(),
        slug: String(draft.slug || '').trim(),
      };
      let res = await fetch(`/api/sites/${site.id}/activity-hubs/${hub.areaId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      let data = await res.json().catch(() => ({}));
      if (res.status === 409 && data?.warning?.code === 'REDIRECT_REQUIRED') {
        const ok = window.confirm(
          `Slug change requires redirect planning.\nOld: ${data?.warning?.oldPath || ''}\nNew: ${data?.warning?.newPath || ''}\n\nProceed and log redirect requirement?`
        );
        if (!ok) {
          setHubSavingId(null);
          return;
        }
        res = await fetch(`/api/sites/${site.id}/activity-hubs/${hub.areaId}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...payload, acknowledgeRedirect: true }),
        });
        data = await res.json().catch(() => ({}));
      }
      if (!res.ok) {
        throw new Error(data?.message || 'Failed to save hub.');
      }
      setClassifierMessage(data?.message || 'Hub saved.');
      await loadHubs();
    } catch (err) {
      setHubsError(err.message || 'Failed to save hub.');
    } finally {
      setHubSavingId(null);
    }
  };

  const setTourOverride = async (hub, tour, mode) => {
    let targetAreaId = null;
    if (mode && typeof mode === 'object') {
      targetAreaId = mode.targetAreaId || null;
      mode = mode.mode;
    }
    const token = localStorage.getItem('token');
    if (!token || !site?.id || !hub?.areaId || !tour?.tourId) return;
    const opKey = `${hub.areaId}:${tour.tourId}:${mode}${targetAreaId ? `:${targetAreaId}` : ''}`;
    setTourOverrideSavingKey(opKey);
    setHubsError('');
    try {
      const res = await fetch(`/api/sites/${site.id}/activity-hubs/${hub.areaId}/tour-overrides`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode,
          targetAreaId,
          tourId: tour.tourId,
          tourTitle: tour.tourTitle || '',
          activityUrl: tour.activityUrl || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to save override.');
      setClassifierMessage(data?.message || 'Tour override updated.');
      await loadHubs();
    } catch (err) {
      setHubsError(err.message || 'Failed to save override.');
    } finally {
      setTourOverrideSavingKey('');
    }
  };

  const setHubEnabled = async (hub, enabled) => {
    const token = localStorage.getItem('token');
    if (!token || !site?.id || !hub?.areaId) return;
    setHubToggleSavingId(hub.areaId);
    setHubsError('');
    try {
      const res = await fetch(`/api/sites/${site.id}/activity-hubs/${hub.areaId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to update hub state.');
      setClassifierMessage(data?.message || 'Hub state updated.');
      await loadHubs();
    } catch (err) {
      setHubsError(err.message || 'Failed to update hub state.');
    } finally {
      setHubToggleSavingId(null);
    }
  };

  const updateTourOverrideFilter = (hubAreaId, patch) => {
    const key = String(hubAreaId || '').trim();
    if (!key) return;
    setTourOverrideFilters((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        ...patch,
      },
    }));
  };

  const updateTourInfoFilter = (column, value) => {
    const key = String(column || '').trim();
    if (!key) return;
    setTourInfoFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearTourInfoFilters = () => {
    setTourInfoSearch('');
    setTourInfoFilters({});
    setTourInfoSort({ column: 'review_count', direction: 'desc' });
  };

  const toggleTourInfoSort = (column) => {
    const key = String(column || '').trim();
    if (!key) return;
    setTourInfoSort((prev) => {
      if (prev?.column === key) {
        return {
          column: key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        column: key,
        direction: 'asc',
      };
    });
  };

  const toggleTourInfoRowSelected = (rowId, checked) => {
    const key = String(rowId || '').trim();
    if (!key) return;
    setTourInfoSelectedRows((prev) => ({
      ...prev,
      [key]: Boolean(checked),
    }));
  };

  const toggleAllVisibleTourInfoRows = (checked) => {
    const nextChecked = Boolean(checked);
    setTourInfoSelectedRows((prev) => {
      const next = { ...prev };
      for (const row of tourInfoDisplayRows) {
        const key = String(row?.id || '').trim();
        if (!key) continue;
        next[key] = nextChecked;
      }
      return next;
    });
  };

  const moveSelectedTourInfoRows = async () => {
    const token = localStorage.getItem('token');
    if (!token || !site?.id) return;
    const targetAreaId = Number.parseInt(String(tourInfoMoveTarget || ''), 10) || null;
    if (!targetAreaId) {
      setTourInfoError('Choose a built activity hub to move the selected tours into.');
      return;
    }

    const entries = Array.from(
      new Map(
        selectedTourInfoRows.map((row) => [
          String(row?.tour_id || '').trim(),
          {
            sourceAreaId: row?.current_activity_hub_area_id || null,
            tourId: String(row?.tour_id || '').trim(),
            tourTitle: String(row?.tour_title || '').trim(),
            activityUrl: String(row?.activity_url || '').trim(),
          },
        ])
      ).values()
    ).filter((entry) => entry.tourId);

    if (!entries.length) {
      setTourInfoError('Select one or more tours first.');
      return;
    }

    setTourInfoMoving(true);
    setTourInfoError('');
    setClassifierMessage('');
    try {
      const res = await fetch(`/api/sites/${site.id}/destination-tours/move-to-hub`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetAreaId,
          entries,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || 'Failed to move selected tours.');
      }
      setClassifierMessage(data?.message || 'Selected tours moved.');
      setTourInfoSelectedRows({});
      setTourInfoMoveTarget('');
      await loadHubs();
      await loadClassifierPreview();
      await loadTourInfo();
    } catch (err) {
      setTourInfoError(err.message || 'Failed to move selected tours.');
    } finally {
      setTourInfoMoving(false);
    }
  };

  const keywordsCount = Number(snapshotStatus?.popularKeywordsRowCount || 0);
  const activityCountTotal =
    Number(snapshotStatus?.topActivitiesRowCount || 0) +
    Number(snapshotStatus?.newActivitiesRowCount || 0) +
    Number(snapshotStatus?.dealsRowCount || 0) +
    Number(snapshotStatus?.htmlActivitiesRowCount || 0);
  const readyToRunClassifier = keywordsCount > 0 && activityCountTotal > 0;

  const isHubDirty = (hub) => {
    const key = String(hub.areaId);
    const draft = hubDrafts[key] || { name: hub.name || '', slug: hub.slug || '' };
    const nameChanged = String(draft.name || '').trim() !== String(hub.name || '').trim();
    const slugChanged = String(draft.slug || '').trim() !== String(hub.slug || '').trim();
    const hasRequired = String(draft.name || '').trim() && String(draft.slug || '').trim();
    return Boolean(hasRequired && (nameChanged || slugChanged));
  };

  const handleNinaProviderChange = (value) => {
    setNinaProvider(normalizeAgentProvider(value));
  };

  const currentAgentSettings = useMemo(
    () => ({
      ninaProvider,
      ninaModel,
      krytenProvider,
      krytenModel,
      polyProvider,
      polyModel,
      gordonProvider,
      gordonModel,
      gordonTextProvider,
      gordonPhotoProvider,
      gordonTextModel,
      gordonPhotoModel,
      reneeProvider,
      reneeModel,
      chrisProvider,
      chrisModel,
      websterProvider,
      websterModel,
    }),
    [
      ninaProvider,
      ninaModel,
      krytenProvider,
      krytenModel,
      polyProvider,
      polyModel,
      gordonProvider,
      gordonModel,
      gordonTextProvider,
      gordonPhotoProvider,
      gordonTextModel,
      gordonPhotoModel,
      reneeProvider,
      reneeModel,
      chrisProvider,
      chrisModel,
      websterProvider,
      websterModel,
    ]
  );

  const agentSettingsDirty = useMemo(() => {
    const lastSaved = lastSavedAgentSettingsRef.current || {};
    return !(
      lastSaved.ninaProvider === currentAgentSettings.ninaProvider &&
      lastSaved.ninaModel === currentAgentSettings.ninaModel &&
      lastSaved.krytenProvider === currentAgentSettings.krytenProvider &&
      lastSaved.krytenModel === currentAgentSettings.krytenModel &&
      lastSaved.polyProvider === currentAgentSettings.polyProvider &&
      lastSaved.polyModel === currentAgentSettings.polyModel &&
      lastSaved.gordonProvider === currentAgentSettings.gordonProvider &&
      lastSaved.gordonModel === currentAgentSettings.gordonModel &&
      lastSaved.gordonTextProvider === currentAgentSettings.gordonTextProvider &&
      lastSaved.gordonPhotoProvider === currentAgentSettings.gordonPhotoProvider &&
      lastSaved.gordonTextModel === currentAgentSettings.gordonTextModel &&
      lastSaved.gordonPhotoModel === currentAgentSettings.gordonPhotoModel &&
      lastSaved.reneeProvider === currentAgentSettings.reneeProvider &&
      lastSaved.reneeModel === currentAgentSettings.reneeModel &&
      lastSaved.chrisProvider === currentAgentSettings.chrisProvider &&
      lastSaved.chrisModel === currentAgentSettings.chrisModel &&
      lastSaved.websterProvider === currentAgentSettings.websterProvider &&
      lastSaved.websterModel === currentAgentSettings.websterModel
    );
  }, [currentAgentSettings]);

  const saveAgentSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAgentSettingsError('Please log in again.');
      setAgentSettingsMessage('');
      return;
    }

    setAgentSettingsSaving(true);
    setAgentSettingsError('');
    setAgentSettingsMessage('');
    try {
      const payload = {
        ninaProvider: currentAgentSettings.ninaProvider,
        ninaModel: currentAgentSettings.ninaModel,
        krytenProvider: currentAgentSettings.krytenProvider,
        krytenModel: currentAgentSettings.krytenModel,
        polyProvider: currentAgentSettings.polyProvider,
        polyModel: currentAgentSettings.polyModel,
        gordonProvider: currentAgentSettings.gordonProvider,
        gordonModel: currentAgentSettings.gordonModel,
        gordonTextProvider: currentAgentSettings.gordonTextProvider,
        gordonPhotoProvider: currentAgentSettings.gordonPhotoProvider,
        gordonTextModel: currentAgentSettings.gordonTextModel,
        gordonPhotoModel: currentAgentSettings.gordonPhotoModel,
        reneeProvider: currentAgentSettings.reneeProvider,
        reneeModel: currentAgentSettings.reneeModel,
        chrisProvider: currentAgentSettings.chrisProvider,
        chrisModel: currentAgentSettings.chrisModel,
        websterProvider: currentAgentSettings.websterProvider,
        websterModel: currentAgentSettings.websterModel,
      };
      console.log('[AI Agents] save requested', {
        payload,
        lastSaved: lastSavedAgentSettingsRef.current,
      });
      const res = await fetch('/api/admin/platform-settings', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      console.log('[AI Agents] save response', {
        ok: res.ok,
        status: res.status,
        data,
      });
      if (!res.ok) {
        throw new Error(data?.message || 'Failed to save AI agent settings.');
      }
      const nextSavedAgentSettings = buildDestinationAgentSettings(data);
      lastSavedAgentSettingsRef.current = nextSavedAgentSettings;
      setNinaProvider(nextSavedAgentSettings.ninaProvider);
      setNinaModel(nextSavedAgentSettings.ninaModel);
      setKrytenProvider(nextSavedAgentSettings.krytenProvider);
      setKrytenModel(nextSavedAgentSettings.krytenModel);
      setPolyProvider(nextSavedAgentSettings.polyProvider);
      setPolyModel(nextSavedAgentSettings.polyModel);
      setGordonProvider(nextSavedAgentSettings.gordonProvider);
      setGordonModel(nextSavedAgentSettings.gordonModel);
      setGordonTextProvider(nextSavedAgentSettings.gordonTextProvider);
      setGordonPhotoProvider(nextSavedAgentSettings.gordonPhotoProvider);
      setGordonTextModel(nextSavedAgentSettings.gordonTextModel);
      setGordonPhotoModel(nextSavedAgentSettings.gordonPhotoModel);
      setReneeProvider(nextSavedAgentSettings.reneeProvider);
      setReneeModel(nextSavedAgentSettings.reneeModel);
      setChrisProvider(nextSavedAgentSettings.chrisProvider);
      setChrisModel(nextSavedAgentSettings.chrisModel);
      setWebsterProvider(nextSavedAgentSettings.websterProvider);
      setWebsterModel(nextSavedAgentSettings.websterModel);
      console.log('[AI Agents] save applied locally', {
        currentAgentSettings: nextSavedAgentSettings,
        lastSaved: lastSavedAgentSettingsRef.current,
      });
      setAgentSettingsMessage('AI agent settings saved for all users.');
    } catch (err) {
      console.warn('[AI Agents] save failed', {
        error: err,
      });
      setAgentSettingsError(err.message || 'Failed to save AI agent settings.');
    } finally {
      setAgentSettingsSaving(false);
    }
  };

  const taxonomyOptions = useMemo(
    () =>
      hubTaxonomy
        .map((item) => ({
          slug: String(item?.slug || '').trim(),
          name: String(item?.name || '').trim(),
        }))
        .filter((item) => item.slug),
    [hubTaxonomy]
  );

  const enabledHubs = useMemo(
    () => hubs.filter((hub) => hub?.isEnabled !== false),
    [hubs]
  );

  const ninaProviderModels = useMemo(
    () => getAgentProviderModels(ninaModelOptions, ninaProvider, ninaModel),
    [ninaModel, ninaModelOptions, ninaProvider]
  );

  const krytenProviderModels = useMemo(
    () => getAgentProviderModels(ninaModelOptions, krytenProvider, krytenModel),
    [krytenModel, krytenProvider, ninaModelOptions]
  );

  const polyProviderModels = useMemo(
    () => getAgentProviderModels(ninaModelOptions, polyProvider, polyModel),
    [ninaModelOptions, polyModel, polyProvider]
  );

  const gordonProviderModels = useMemo(
    () => getAgentProviderModels(ninaModelOptions, gordonProvider, gordonModel),
    [gordonModel, gordonProvider, ninaModelOptions]
  );

  const gordonTextProviderModels = useMemo(
    () => getAgentProviderModels(ninaModelOptions, gordonTextProvider, gordonTextModel),
    [gordonTextModel, gordonTextProvider, ninaModelOptions]
  );

  const gordonPhotoProviderModels = useMemo(
    () => getAgentProviderModels(ninaModelOptions, gordonPhotoProvider, gordonPhotoModel),
    [gordonPhotoModel, gordonPhotoProvider, ninaModelOptions]
  );

  const reneeProviderModels = useMemo(
    () => getAgentProviderModels(ninaModelOptions, reneeProvider, reneeModel),
    [ninaModelOptions, reneeModel, reneeProvider]
  );

  const chrisProviderModels = useMemo(
    () => getAgentImageProviderModels(chrisModelOptions, chrisProvider, chrisModel),
    [chrisModel, chrisModelOptions, chrisProvider]
  );

  const websterProviderModels = useMemo(
    () => getAgentProviderModels(ninaModelOptions, websterProvider, websterModel),
    [ninaModelOptions, websterModel, websterProvider]
  );

  const disabledHubs = useMemo(
    () => hubs.filter((hub) => hub?.isEnabled === false),
    [hubs]
  );

  const hubTourOverrideMeta = useMemo(() => {
    const next = {};
    for (const hub of hubs) {
      const key = String(hub?.areaId || '');
      if (!key) continue;
      const allTours = Array.isArray(hub?.overrideTours) && hub.overrideTours.length
        ? hub.overrideTours
        : Array.isArray(hub?.matchedTours)
          ? hub.matchedTours
          : [];
      const locationOptions = buildTourOverrideLocationOptions({
        tours: allTours,
        siteName: site?.name || '',
        neighboringDestinations,
      });
      const filters = tourOverrideFilters[key] || {};
      const query = String(filters.query || '').trim();
      const normalizedQuery = normalizeFilterText(query);
      const selectedLocation = String(filters.location || '').trim();
      const sortBy = String(filters.sortBy || 'reviews_desc').trim() || 'reviews_desc';
      const manuallyIncludedTourIds = getHubToursManuallyIncluded(hub);
      const manuallyIncludedTourIdSet = new Set(manuallyIncludedTourIds);
      const ninaSelectedTourIds = getHubToursSelectedForNina(hub);
      const ninaSelectedTourIdSet = new Set(ninaSelectedTourIds);
      const filteredBaseTours = allTours.filter((tour) => {
        const tourId = String(tour?.tourId || tour?.tour_id || '').trim();
        if (sortBy === 'included' && !manuallyIncludedTourIdSet.has(tourId)) return false;
        if (sortBy === 'published' && !ninaSelectedTourIdSet.has(tourId)) return false;
        const title = String(tour?.tourTitle || '');
        if (normalizedQuery && !normalizeFilterText(title).includes(normalizedQuery)) return false;
        if (selectedLocation) {
          const compactTitle = normalizeCompactFilterText(title);
          if (!compactTitle.includes(selectedLocation)) return false;
        }
        return true;
      });
      const filteredTours = sortBy === 'included'
        ? manuallyIncludedTourIds
          .map((tourId) =>
            filteredBaseTours.find((tour) => String(tour?.tourId || tour?.tour_id || '').trim() === tourId) || null
          )
          .filter(Boolean)
        : sortBy === 'published'
        ? ninaSelectedTourIds
          .map((tourId) =>
            filteredBaseTours.find((tour) => String(tour?.tourId || tour?.tour_id || '').trim() === tourId) || null
          )
          .filter(Boolean)
        : sortTourOverrideRows(filteredBaseTours, sortBy);
      next[key] = {
        allTours,
        filteredTours,
        locationOptions,
        query,
        location: selectedLocation,
        sortBy,
        manuallyIncludedTourIds,
        ninaSelectedTourIds,
      };
    }
    return next;
  }, [hubs, neighboringDestinations, site?.name, tourOverrideFilters]);

  const classifierPreviewHubs = Array.isArray(classifierPreview?.hubs) ? classifierPreview.hubs : [];

  const canonicalHubMoveTargets = useMemo(() => {
    const builtBySlug = new Map(
      hubs
        .map((hub) => [String(hub?.slug || '').trim(), hub])
        .filter(([slug]) => slug)
    );
    return classifierPreviewHubs
      .filter((hub) => hub?.isCanonical)
      .map((hub) => {
        const slug = String(hub?.slug || '').trim();
        const built = builtBySlug.get(slug) || null;
        const areaId = Number(hub?.builtAreaId || built?.areaId || 0) || null;
        return {
          slug,
          name: String(hub?.name || built?.name || slug).trim() || slug,
          areaId,
          isBuilt: Boolean(areaId),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [classifierPreviewHubs, hubs]);

  const tourInfoHubMembershipByTourId = useMemo(() => {
    const membership = new Map();
    for (const hub of hubs) {
      const hubAreaId = Number(hub?.areaId || 0) || null;
      if (!hubAreaId) continue;
      const hubName = String(hub?.name || hub?.slug || '').trim();
      const hubSlug = String(hub?.slug || '').trim();
      const sourceTours = Array.isArray(hub?.overrideTours) && hub.overrideTours.length
        ? hub.overrideTours
        : Array.isArray(hub?.matchedTours)
          ? hub.matchedTours
          : [];
      for (const tour of sourceTours) {
        const tourId = String(tour?.tourId || '').trim();
        if (!tourId) continue;
        const bucket = membership.get(tourId) || [];
        if (!bucket.some((entry) => entry.areaId === hubAreaId)) {
          bucket.push({
            areaId: hubAreaId,
            name: hubName,
            slug: hubSlug,
          });
        }
        membership.set(tourId, bucket);
      }
    }
    return membership;
  }, [hubs]);

  const tourInfoDisplayColumns = useMemo(
    () => ['current_activity_hub', ...tourInfoColumns],
    [tourInfoColumns]
  );

  const allTourInfoDisplayRows = useMemo(
    () =>
      tourInfoRows.map((row) => {
        const tourId = String(row?.tour_id || '').trim();
        const memberships = tourId ? tourInfoHubMembershipByTourId.get(tourId) || [] : [];
        const currentHubLabel = memberships.length
          ? memberships.map((entry) => entry.name || entry.slug).join(', ')
          : '';
        return {
          ...row,
          current_activity_hub: currentHubLabel,
          current_activity_hub_area_id: memberships.length === 1 ? memberships[0].areaId : null,
          current_activity_hub_count: memberships.length,
        };
      }),
    [tourInfoRows, tourInfoHubMembershipByTourId]
  );

  const tourInfoUniqueValuesByColumn = useMemo(() => {
    const next = {};
    for (const column of tourInfoDisplayColumns) {
      next[column] = getDatabaseColumnUniqueValues(allTourInfoDisplayRows, column);
    }
    return next;
  }, [allTourInfoDisplayRows, tourInfoDisplayColumns]);

  const tourInfoDisplayRows = useMemo(() => {
    const normalizedSearch = normalizeFilterText(tourInfoSearch);
    const activeFilters = Object.entries(tourInfoFilters || {}).filter(([, value]) => String(value || '').trim());
    const rows = allTourInfoDisplayRows.filter((row) => {
      if (normalizedSearch) {
        const haystack = tourInfoDisplayColumns
          .map((column) => normalizeFilterText(getDatabaseCellText(row?.[column])))
          .join(' ');
        if (!haystack.includes(normalizedSearch)) return false;
      }
      for (const [column, value] of activeFilters) {
        const cellText = getDatabaseCellText(row?.[column]).toLowerCase();
        if (!cellText.includes(String(value || '').trim().toLowerCase())) return false;
      }
      return true;
    });

    const sortColumn = String(tourInfoSort?.column || '').trim();
    const direction = String(tourInfoSort?.direction || 'asc').toLowerCase() === 'desc' ? 'desc' : 'asc';
    if (!sortColumn) return rows;
    return [...rows].sort((a, b) => {
      const diff = compareDatabaseValues(a?.[sortColumn], b?.[sortColumn]);
      return direction === 'desc' ? -diff : diff;
    });
  }, [allTourInfoDisplayRows, tourInfoDisplayColumns, tourInfoFilters, tourInfoSearch, tourInfoSort]);

  const selectedTourInfoRows = useMemo(
    () => tourInfoDisplayRows.filter((row) => tourInfoSelectedRows[String(row?.id || '')]),
    [tourInfoDisplayRows, tourInfoSelectedRows]
  );

  const allVisibleTourInfoRowsSelected = useMemo(
    () =>
      tourInfoDisplayRows.length > 0 &&
      tourInfoDisplayRows.every((row) => tourInfoSelectedRows[String(row?.id || '')]),
    [tourInfoDisplayRows, tourInfoSelectedRows]
  );

  const buildEligibleCanonicalHubs = useMemo(
    () => classifierPreviewHubs.filter((hub) => hub?.buildEligible),
    [classifierPreviewHubs]
  );
  const projectedThreshold = Math.max(1, Number.parseInt(globalThresholdDraft, 10) || 1);

  const projectedCanonicalHubs = useMemo(() => {
    const map = new Map();
    const mergeTours = (existing = [], incoming = []) => {
      const next = new Map();
      for (const tour of [...existing, ...incoming]) {
        const key = String(tour?.tourId || '').trim() || `${tour?.tourTitle || ''}:${tour?.activityUrl || ''}`;
        if (!key) continue;
        const current = next.get(key);
        if (!current) {
          next.set(key, tour);
          continue;
        }
        const currentReviews = Number(current?.reviewCount || 0);
        const incomingReviews = Number(tour?.reviewCount || 0);
        if (incomingReviews > currentReviews) next.set(key, tour);
      }
      return Array.from(next.values())
        .sort((a, b) => {
          const br = Number(b?.reviewCount || 0);
          const ar = Number(a?.reviewCount || 0);
          if (br !== ar) return br - ar;
          return Number(a?.partnerRank || 999) - Number(b?.partnerRank || 999);
        })
        .slice(0, 10);
    };
    const upsert = (hub, targetSlug, targetName) => {
      const key = String(targetSlug || '').trim();
      if (!key) return;
      const existing = map.get(key) || {
        slug: key,
        name: targetName || hub?.name || key,
        activitiesCount: 0,
        score: 0,
        tours: [],
        sourcePhrases: [],
      };
      existing.name = targetName || existing.name;
      existing.activitiesCount += Number(hub?.activitiesCount || 0);
      existing.score += Number(hub?.score || 0);
      existing.tours = mergeTours(existing.tours, hub?.tours || hub?.matchedTours || []);
      existing.sourcePhrases = Array.from(
        new Set([...(existing.sourcePhrases || []), ...(hub?.debugSourcePhrases || [])])
      );
      map.set(key, existing);
    };

    for (const hub of classifierPreviewHubs.filter((entry) => entry?.isCanonical)) {
      const targetSlug = String(hub?.debugCanonicalMatch?.slug || hub?.slug || '').trim();
      const targetName = String(hub?.debugCanonicalMatch?.name || hub?.name || '').trim();
      upsert(hub, targetSlug, targetName);
    }

    for (const hub of classifierPreviewHubs.filter((entry) => !entry?.isCanonical)) {
      const action = suggestionActions[String(hub?.slug || '').trim()];
      if (!action?.targetSlug) continue;
      upsert(hub, action.targetSlug, action.targetName || action.targetSlug);
    }

    return Array.from(map.values())
      .filter((hub) => Number(hub?.activitiesCount || 0) >= projectedThreshold)
      .sort((a, b) => {
        const scoreDiff = Number(b?.score || 0) - Number(a?.score || 0);
        if (scoreDiff !== 0) return scoreDiff;
        return Number(b?.activitiesCount || 0) - Number(a?.activitiesCount || 0);
      });
  }, [classifierPreviewHubs, suggestionActions, projectedThreshold]);

  const suggestedCanonicalSlugs = useMemo(() => {
    const existing = new Set(
      hubTaxonomy
        .map((item) => String(item?.slug || '').trim())
        .filter(Boolean)
    );
    const next = [];
    const seen = new Set();
    for (const hub of classifierPreviewHubs) {
      const slug = String(hub?.slug || '').trim();
      const name = String(hub?.name || '').trim();
      if (!slug || !name) continue;
      if (hub?.isCanonical) continue;
      if (dismissedSuggestedSlugs.includes(slug)) continue;
      if (existing.has(slug) || seen.has(slug)) continue;
      const keywords = Array.isArray(hub?.debugKeywords) ? hub.debugKeywords : [];
      const sourcePhrases = Array.isArray(hub?.debugSourcePhrases) ? hub.debugSourcePhrases : [];
      const synonymsSeed = sourcePhrases.length ? sourcePhrases : keywords;
      next.push({
        slug,
        name,
        synonyms: synonymsSeed.slice(0, 8).join(', '),
        activitiesCount: Number(hub?.activitiesCount || 0),
        score: hub?.score ?? null,
        debugKeywords: keywords,
        debugSourcePhrases: sourcePhrases,
        debugCanonicalMatch: hub?.debugCanonicalMatch || null,
      });
      seen.add(slug);
    }
    next.sort((a, b) => Number(b.activitiesCount || 0) - Number(a.activitiesCount || 0));
    return next;
  }, [hubTaxonomy, classifierPreviewHubs, dismissedSuggestedSlugs]);

  useEffect(() => {
    setSuggestedSlugDrafts((prev) => {
      const next = {};
      for (const suggestion of suggestedCanonicalSlugs) {
        const key = suggestion.slug;
        const existing = prev[key];
        next[key] = {
          slug: existing?.slug ?? suggestion.slug,
          name: existing?.name ?? suggestion.name,
          synonyms: existing?.synonyms ?? suggestion.synonyms,
          targetCanonicalSlug: existing?.targetCanonicalSlug ?? '',
        };
      }
      return next;
    });
  }, [suggestedCanonicalSlugs]);

  const updateSuggestedSlugDraft = (originalSlug, patch) => {
    const key = String(originalSlug || '').trim();
    if (!key) return;
    setSuggestedSlugDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        ...patch,
      },
    }));
  };

  const addSuggestedSlug = (suggestion) => {
    const key = String(suggestion?.slug || '').trim();
    const draft = key ? suggestedSlugDrafts[key] || {} : {};
    const normalizedSlug = toSlugSegment(draft.slug || suggestion?.slug || '');
    const normalizedName = String(draft.name || suggestion?.name || normalizedSlug).trim();
    const normalizedSynonyms = String(draft.synonyms || suggestion?.synonyms || '').trim();
    if (!normalizedSlug || !normalizedName) return;
    setHubTaxonomy((prev) => {
      const exists = prev.some((item) => String(item?.slug || '').trim() === normalizedSlug);
      if (exists) return prev;
      return [
        ...prev,
        {
          slug: normalizedSlug,
          name: normalizedName,
          synonyms: normalizedSynonyms,
        },
      ];
    });
    setSuggestionActions((prev) => ({
      ...prev,
      [key]: {
        type: 'new',
        targetSlug: normalizedSlug,
        targetName: normalizedName,
      },
    }));
    setDismissedSuggestedSlugs((prev) =>
      prev.includes(key) ? prev : [...prev, key]
    );
  };

  const addAllSuggestedSlugs = () => {
    for (const suggestion of suggestedCanonicalSlugs) {
      addSuggestedSlug(suggestion);
    }
  };

  const mergeSuggestedIntoCanonical = (suggestion) => {
    const key = String(suggestion?.slug || '').trim();
    if (!key) return;
    const draft = suggestedSlugDrafts[key] || {};
    const targetSlug = toSlugSegment(draft.targetCanonicalSlug || '');
    if (!targetSlug) return;
    const mergedSource = [
      String(draft.slug || suggestion?.slug || '').trim(),
      String(draft.name || suggestion?.name || '').trim(),
      ...String(draft.synonyms || suggestion?.synonyms || '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean),
    ].filter(Boolean);
    const sourceUnique = Array.from(new Set(mergedSource));
    setHubTaxonomy((prev) =>
      prev.map((row) => {
        const rowSlug = String(row?.slug || '').trim();
        if (rowSlug !== targetSlug) return row;
        const existing = String(row?.synonyms || '')
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean);
        const merged = Array.from(new Set([...existing, ...sourceUnique]));
        return {
          ...row,
          synonyms: merged.join(', '),
        };
      })
    );
    const targetName =
      taxonomyOptions.find((option) => option.slug === targetSlug)?.name || targetSlug;
    setSuggestionActions((prev) => ({
      ...prev,
      [key]: {
        type: 'merge',
        targetSlug,
        targetName,
      },
    }));
    setDismissedSuggestedSlugs((prev) =>
      prev.includes(key) ? prev : [...prev, key]
    );
  };

  const sendSingleHubToNina = async (hub) => {
    const token = localStorage.getItem('token');
    if (!token || !site?.id || !hub?.areaId) return;
    setNinaSendingHubId(hub.areaId);
    setNinaMessage('');
    setNinaError('');
    try {
      const res = await fetch(`/api/sites/${site.id}/activity-hubs/${hub.areaId}/send-to-nina`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: ninaProvider,
          model: ninaModel,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to send hub to Nina.');
      setNinaMessage(`Nina content received for ${data?.hub?.name || 'hub'}.`);
      await loadHubs();
    } catch (err) {
      setNinaError(err.message || 'Failed to send hub to Nina.');
      await loadHubs();
    } finally {
      setNinaSendingHubId(null);
    }
  };

  const sendAllHubsToNina = async () => {
    const token = localStorage.getItem('token');
    if (!token || !site?.id) return;
    setNinaSendingAll(true);
    setNinaMessage('');
    setNinaError('');
    try {
      const res = await fetch(`/api/sites/${site.id}/activity-hubs/send-to-nina`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: ninaProvider,
          model: ninaModel,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to send hubs to Nina.');
      const nextResults = Array.isArray(data?.results) ? data.results : [];
      setNinaMessage(
        `Nina run finished. ${Number(data?.successCount || 0)} succeeded, ${Number(data?.errorCount || 0)} failed.`
      );
      if (nextResults.some((item) => !item?.ok)) {
        setNinaError('One or more hubs failed. Check each hub card for details.');
      }
      await loadHubs();
    } catch (err) {
      setNinaError(err.message || 'Failed to send hubs to Nina.');
      await loadHubs();
    } finally {
      setNinaSendingAll(false);
    }
  };

  const publishHubNina = async (hub, publishMode = 'update') => {
    const token = localStorage.getItem('token');
    if (!token || !site?.id || !hub?.areaId) return;
    setNinaPublishingHubId(hub.areaId);
    setNinaMessage('');
    setNinaError('');
    const widgetDraft = hubWidgetDrafts[String(hub.areaId)] || {};
    try {
      const res = await fetch(`/api/sites/${site.id}/activity-hubs/${hub.areaId}/publish-nina`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          revisionId: hub.currentOutputRevisionId || hub.ninaRevisionId || null,
          publishMode,
          autoWidgetConfig: {
            query: String(widgetDraft.query || '').trim(),
            localeCode: String(widgetDraft.localeCode || '').trim() || 'en-US',
            numberOfItems: Math.max(1, Math.min(50, Number.parseInt(String(widgetDraft.numberOfItems || 20), 10) || 20)),
            campaignId: String(widgetDraft.campaignId || '').trim(),
            partnerId: String(widgetDraft.partnerId || '').trim() || 'BEWYOUX',
            updateExisting: Boolean(widgetDraft.updateExisting),
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to publish Nina content.');
      setNinaMessage(
        data?.plaque?.landingPath
          ? `${publishMode === 'scratch' ? 'Republished' : 'Updated'} ${hub.name}: ${data.plaque.landingPath}`
          : `Published Nina content for ${hub.name}.`
      );
      await loadHubs();
    } catch (err) {
      setNinaError(err.message || 'Failed to publish Nina content.');
    } finally {
      setNinaPublishingHubId(null);
    }
  };

  const humanizeHubWithKryten = async (hub) => {
    const token = localStorage.getItem('token');
    if (!token || !site?.id || !hub?.areaId) return;
    setKrytenHumanizingHubId(hub.areaId);
    setKrytenMessage('');
    setKrytenError('');
    try {
      const res = await fetch(`/api/sites/${site.id}/activity-hubs/${hub.areaId}/humanize`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: krytenProvider,
          model: krytenModel,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to humanize Nina content.');
      setKrytenMessage(`Kryten humanized ${data?.hub?.name || hub.name || 'hub'}.`);
      await loadHubs();
    } catch (err) {
      setKrytenError(err.message || 'Failed to humanize Nina content.');
      await loadHubs();
    } finally {
      setKrytenHumanizingHubId(null);
    }
  };

  const openPlaqueEditor = (hub) => {
    const plaqueId = Number(hub?.plaqueId || 0);
    if (!plaqueId) return;
    const params = new URLSearchParams();
    params.set('tab', 'Creative Studio');
    params.set('creativeStudioView', 'plaques');
    params.set('plaqueId', String(plaqueId));
    history.push(`/control-panel?${params.toString()}`);
  };

  const isHubPublished = (hub) => Boolean(hub?.publishedRevisionId || hub?.plaqueLandingKey);

  const isHubCardExpanded = (hub) => {
    const key = String(hub?.areaId || '').trim();
    if (!key) return true;
    if (Object.prototype.hasOwnProperty.call(hubCardExpanded, key)) {
      return Boolean(hubCardExpanded[key]);
    }
    return !isHubPublished(hub);
  };

  const toggleHubCardExpanded = (hub) => {
    const key = String(hub?.areaId || '').trim();
    if (!key) return;
    setHubCardExpanded((prev) => ({
      ...prev,
      [key]: !isHubCardExpanded(hub),
    }));
  };

  const isHubPanelOpen = (hubAreaId, panelName) =>
    Boolean(hubPanelExpanded[`${hubAreaId}:${panelName}`]);

  const toggleHubPanel = (hubAreaId, panelName) => {
    const key = `${hubAreaId}:${panelName}`;
    setHubPanelExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="space-y-4">
      {!isDestination && (
        <div className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 p-3 rounded">
          This site is currently type <strong>{site.site_type || 'venue'}</strong>. Saving here converts it to a destination.
        </div>
      )}

      {!hideSetupTabs && (
        <div className="border rounded p-1 bg-black/20">
          <div className="flex flex-wrap gap-1">
            {setupTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                className={`px-3 py-2 rounded text-sm ${
                  activeSetupTab === tab ? 'bg-blue-700 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                }`}
                onClick={() => setActiveSetupTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeSetupTab === 'Destination Setup' && (
      <div className="space-y-2">
        <h3 className="font-semibold text-gray-100">Destination Setup</h3>
        <p className="text-sm text-gray-300">
          Configure the base destination identity and URL parts. Keep these stable once QR codes are printed.
        </p>
        <label className="block text-sm font-medium text-gray-200">Destination Name</label>
        <input
          type="text"
          className="border p-2 w-full"
          placeholder="Destination name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="text-xs text-gray-300">Public destination title, e.g. "Da Nang" or "Te Anau".</div>
        <label className="block text-sm font-medium text-gray-200">Country Code (ISO-2)</label>
        <input
          type="text"
          className="border p-2 w-full"
          placeholder="Country code (ISO-2, e.g. vn)"
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value)}
        />
        <div className="text-xs text-gray-300">Two letters only, lowercase preferred: `vn`, `nz`, `au`.</div>
        <label className="block text-sm font-medium text-gray-200">Destination Slug</label>
        <input
          type="text"
          className="border p-2 w-full"
          placeholder="Destination slug"
          value={destinationSlug}
          onChange={(e) => setDestinationSlug(e.target.value)}
          disabled={slugsLocked}
        />
        <div className="text-xs text-gray-300">
          Middle URL segment. Example: `/explore/vn/<strong>danang</strong>/cycling-tours`.
        </div>
        <label className="block text-sm font-medium text-gray-200">Site Slug</label>
        <input
          type="text"
          className="border p-2 w-full"
          placeholder="Site slug"
          value={siteSlug}
          onChange={(e) => setSiteSlug(e.target.value)}
          disabled={slugsLocked}
        />
        <div className="text-xs text-gray-300">
          Destination landing page slug. Usually `overview` or another permanent destination landing term.
        </div>
        <label className="block text-sm font-medium text-gray-200">Hub Slug (Optional)</label>
        <input
          type="text"
          className="border p-2 w-full"
          placeholder="Hub slug (optional)"
          value={hubSlug}
          onChange={(e) => setHubSlug(e.target.value)}
        />
        <div className="text-xs text-gray-300">
          Optional override slug for special cases. Leave blank unless you explicitly need it.
        </div>
        <label className="block text-sm font-medium text-gray-200">Hub Status</label>
        <select
          className="border p-2 w-full"
          value={hubStatus}
          onChange={(e) => setHubStatus(e.target.value)}
        >
          <option value="unclassified">unclassified</option>
          <option value="draft">draft</option>
          <option value="ready">ready</option>
        </select>
        <div className="text-xs text-gray-300">
          `unclassified` = not enough data, `draft` = partial, `ready` = publishing-ready.
        </div>
        <label className="block text-sm font-medium text-gray-200">Threshold Override (Optional)</label>
        <input
          type="number"
          min="1"
          className="border p-2 w-full"
          placeholder="Threshold override (optional)"
          value={thresholdOverride}
          onChange={(e) => setThresholdOverride(e.target.value)}
        />
        <div className="text-xs text-gray-300">
          Per-destination minimum activities required for a hub to count as ready. Leave empty to use global default.
        </div>
        <div className="rounded border border-gray-800 bg-gray-900/60 p-4 space-y-3">
          <label className="block text-sm font-medium text-gray-200">
            Destination GetYourGuide Location ID <span className="text-amber-300">*</span>
          </label>
          <input
            type="text"
            className="border p-2 w-full"
            placeholder="Required for the primary city widget"
            value={gygLocationId}
            onChange={(e) => setGygLocationId(e.target.value)}
          />
          <div className="text-xs text-gray-300">
            Used for the current destination city widget that appears first at the bottom of each activity page.
          </div>
          <div>
            <div className="text-sm font-medium text-gray-200">Neighbouring Destinations</div>
            <div className="text-xs text-gray-300">
              Ordered by distance from this destination. The activity pages will append the source destination first, then use this ordered list for the remaining city widgets.
            </div>
          </div>
          <label className="block text-sm font-medium text-gray-200">Activity Page City Widgets</label>
          <input
            type="number"
            min="0"
            max="10"
            className="border p-2 w-full"
            value={activityPageCityWidgetCount}
            onChange={(e) => setActivityPageCityWidgetCount(e.target.value)}
          />
          <div className="text-xs text-gray-300">
            Count includes the current destination widget. Example: `3` = current destination + first 2 neighbouring destinations.
          </div>
          <div className="space-y-3">
            {neighboringDestinations.map((entry, index) => (
              <div key={`neighboring-destination-${index}`} className="rounded border border-gray-800 bg-gray-950/70 p-3 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-300">
                    Neighbouring Destination {index + 1}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="rounded bg-gray-800 px-2 py-1 text-xs text-white disabled:opacity-40"
                      onClick={() =>
                        setNeighboringDestinations((prev) => moveListItem(prev, index, index - 1))
                      }
                      disabled={index === 0}
                    >
                      Move Up
                    </button>
                    <button
                      type="button"
                      className="rounded bg-gray-800 px-2 py-1 text-xs text-white disabled:opacity-40"
                      onClick={() =>
                        setNeighboringDestinations((prev) => moveListItem(prev, index, index + 1))
                      }
                      disabled={index === neighboringDestinations.length - 1}
                    >
                      Move Down
                    </button>
                    <button
                      type="button"
                      className="rounded bg-gray-800 px-2 py-1 text-xs text-white"
                      onClick={() =>
                        setNeighboringDestinations((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <label className="block text-sm font-medium text-gray-200">Destination Name</label>
                <input
                  type="text"
                  className="border p-2 w-full"
                  placeholder="Hoi An"
                  value={entry.name}
                  onChange={(e) =>
                    setNeighboringDestinations((prev) =>
                      prev.map((item, itemIndex) => (
                        itemIndex === index ? { ...item, name: e.target.value } : item
                      ))
                    )
                  }
                />
                <label className="block text-sm font-medium text-gray-200">
                  GetYourGuide Location ID <span className="text-amber-300">*</span>
                </label>
                <input
                  type="text"
                  className="border p-2 w-full"
                  placeholder="Required"
                  value={entry.locationId}
                  onChange={(e) =>
                    setNeighboringDestinations((prev) =>
                      prev.map((item, itemIndex) => (
                        itemIndex === index ? { ...item, locationId: e.target.value } : item
                      ))
                    )
                  }
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            className="bg-gray-800 text-white px-3 py-2 rounded"
            onClick={() =>
              setNeighboringDestinations((prev) => (
                prev.length >= 10 ? prev : [...prev, createEmptyNeighboringDestination()]
              ))
            }
            disabled={neighboringDestinations.length >= 10}
          >
            {neighboringDestinations.length >= 10 ? 'Maximum 10 Destinations Added' : 'Add Neighbouring Destination'}
          </button>
          <div className="text-xs text-gray-300">
            These IDs are now required so the city widgets always resolve to the correct destination feed.
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={slugsLocked}
            onChange={(e) => setSlugsLocked(e.target.checked)}
          />
          <span className="text-gray-200">Lock slugs (recommended after pages/QRs are published)</span>
        </label>
        <div className="text-xs text-gray-300">
          When enabled, URL slugs are protected from accidental edits.
        </div>

        <div className="text-xs text-gray-200">Canonical preview: {canonicalPath || 'n/a'}</div>

        <button
          type="button"
          className="bg-green-600 text-white px-4 py-2 rounded"
          onClick={saveSite}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save Destination Settings'}
        </button>
        {message && <div className="text-sm text-green-700">{message}</div>}
        {error && <div className="text-sm text-red-700">{error}</div>}
      </div>
      )}

      {activeSetupTab === 'CSV Import & Classifier' && (
      <div className="space-y-2">
        <h3 className="font-semibold text-gray-100">CSV Import & Classifier</h3>
        <p className="text-sm text-gray-300">
          Upload keywords plus at least one activity source. Activity sources can come from CSVs or saved GetYourGuide HTML listing pages.
        </p>
        <div className="text-sm text-gray-200">
          Snapshot status:{' '}
          {snapshotLoading
            ? 'Loading...'
            : snapshotStatus
              ? `keywords ${snapshotStatus.popularKeywordsRowCount || 0}, top ${snapshotStatus.topActivitiesRowCount || 0}, new ${snapshotStatus.newActivitiesRowCount || 0}, deals ${snapshotStatus.dealsRowCount || 0}, html ${snapshotStatus.htmlActivitiesRowCount || 0}`
              : 'No snapshot yet'}
        </div>
        <div className="text-sm text-gray-200">
          Database status:{' '}
          {snapshotLoading
            ? 'Loading...'
            : snapshotStatus
              ? `${snapshotStatus.databaseTotalTours || 0} unique tours currently stored for this destination`
              : 'No tours stored yet'}
        </div>
        {snapshotError && <div className="text-sm text-red-700">{snapshotError}</div>}

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-200">Popular Keywords CSV</label>
          <div className="text-xs text-gray-300">
            Expected columns include Tour Tag Name, Bookings Index Measure, Demand index.
          </div>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setKeywordsFile(e.target.files?.[0] || null)}
          />
          <button
            type="button"
            className="bg-blue-600 text-white px-4 py-2 rounded"
            disabled={!keywordsFile || uploadingKeywords}
            onClick={() => uploadCsv('keywords')}
          >
            {uploadingKeywords ? 'Uploading...' : 'Upload Keywords CSV'}
          </button>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-200">Top Activities CSV</label>
          <div className="text-xs text-gray-300">
            Expected columns include Tour ID, Tour Title, Rank, Price, Rating, Reviews.
          </div>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setActivitiesFile(e.target.files?.[0] || null)}
          />
          <button
            type="button"
            className="bg-blue-600 text-white px-4 py-2 rounded"
            disabled={!activitiesFile || uploadingActivities}
            onClick={() => uploadCsv('activities')}
          >
            {uploadingActivities ? 'Uploading...' : 'Upload Activities CSV'}
          </button>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-200">New Activities CSV (Last 30 Days)</label>
          <div className="text-xs text-gray-300">
            Optional but recommended. Adds fresh tours and marks them as <strong>NEW</strong>.
          </div>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setNewActivitiesFile(e.target.files?.[0] || null)}
          />
          <button
            type="button"
            className="bg-blue-600 text-white px-4 py-2 rounded"
            disabled={!newActivitiesFile || uploadingNewActivities}
            onClick={() => uploadCsv('new_activities')}
          >
            {uploadingNewActivities ? 'Uploading...' : 'Upload New Activities CSV'}
          </button>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-200">Deals & Promotions CSV</label>
          <div className="text-xs text-gray-300">
            Optional but recommended. Active deals are tagged with discount badges and discounts auto-expire based on `From/To`.
          </div>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setDealsFile(e.target.files?.[0] || null)}
          />
          <button
            type="button"
            className="bg-blue-600 text-white px-4 py-2 rounded"
            disabled={!dealsFile || uploadingDeals}
            onClick={() => uploadCsv('deals')}
          >
            {uploadingDeals ? 'Uploading...' : 'Upload Deals CSV'}
          </button>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-200">Upload HTML</label>
          <div className="text-xs text-gray-300">
            Upload saved GetYourGuide destination listing pages (`.html`, `.htm`, or `.txt`). These import as a dedicated HTML activity source, deduplicate by tour, and update any tours already saved from earlier HTML uploads.
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              multiple
              accept=".html,.htm,.txt,text/html,text/plain"
              onChange={(e) => setHtmlFiles(Array.from(e.target.files || []))}
            />
            <button
              type="button"
              className="bg-blue-600 text-white px-4 py-2 rounded"
              disabled={!htmlFiles.length || uploadingHtml}
              onClick={uploadHtml}
            >
              {uploadingHtml ? 'Uploading...' : 'Upload HTML'}
            </button>
          </div>
          <div className="text-xs text-gray-400">
            {htmlFiles.length
              ? `${htmlFiles.length} file(s) selected`
              : 'You can select one file or a batch from different GYG searches.'}
          </div>
        </div>

        <button
          type="button"
          className="bg-purple-700 text-white px-4 py-2 rounded"
          disabled={!readyToRunClassifier || classifierRunning}
          onClick={runClassifier}
        >
          {classifierRunning ? 'Running classifier...' : 'Run Classifier'}
        </button>
        {!readyToRunClassifier && (
          <div className="text-xs text-gray-500">
            Upload Keywords CSV plus at least one activity source (Top, New, Deals, or HTML) before running the classifier.
            Current counts: keywords={keywordsCount}, activities={activityCountTotal}.
          </div>
        )}
        {classifierMessage && <div className="text-sm text-green-700">{classifierMessage}</div>}
        {classifierError && <div className="text-sm text-red-700">{classifierError}</div>}
      </div>
      )}

      {activeSetupTab === 'Tour Info' && (
      <div className="space-y-4">
        <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-slate-950/85 p-5 shadow-[0_20px_50px_rgba(8,47,73,0.35)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.18),_transparent_36%),radial-gradient(circle_at_bottom_right,_rgba(16,185,129,0.14),_transparent_32%)]" />
          <div className="relative space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-200/80">
                  Tour Database Browser
                </div>
                <h3 className="mt-2 text-2xl font-semibold text-white">Tour Info</h3>
                <p className="mt-2 max-w-3xl text-sm text-slate-300">
                  Browse every stored tour row for this destination snapshot, search across the whole dataset, and filter individual columns the way you would in a spreadsheet.
                </p>
              </div>
              <button
                type="button"
                className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-medium text-cyan-100 hover:bg-cyan-500/20"
                onClick={loadTourInfo}
                disabled={tourInfoLoading}
              >
                {tourInfoLoading ? 'Refreshing...' : 'Refresh Tours'}
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Snapshot</div>
                <div className="mt-2 text-2xl font-semibold text-white">
                  {tourInfoSnapshot?.id || snapshotStatus?.id || 'n/a'}
                </div>
                <div className="mt-1 text-xs text-slate-400">
                  Latest working dataset for this destination
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Rows Visible</div>
                <div className="mt-2 text-2xl font-semibold text-white">
                  {tourInfoDisplayRows.length}
                  <span className="ml-2 text-sm font-medium text-slate-400">/ {tourInfoRows.length}</span>
                </div>
                <div className="mt-1 text-xs text-slate-400">
                  Filtered result set from the destination activity table
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Search & Filters</div>
                <div className="mt-2 text-2xl font-semibold text-white">
                  {Object.values(tourInfoFilters).filter((value) => String(value || '').trim()).length}
                </div>
                <div className="mt-1 text-xs text-slate-400">
                  Active column filters {tourInfoSearch ? 'plus global search' : 'currently applied'}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Selected</div>
                <div className="mt-2 text-2xl font-semibold text-white">
                  {selectedTourInfoRows.length}
                </div>
                <div className="mt-1 text-xs text-slate-400">
                  Tours queued for bulk move
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(260px,1.2fr)_auto]">
              <input
                type="text"
                className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
                placeholder="Search across all columns: title, source, city, country, URLs, IDs..."
                value={tourInfoSearch}
                onChange={(e) => setTourInfoSearch(e.target.value)}
              />
              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs text-slate-300">
                  Sorted by <span className="font-semibold text-white">{formatDatabaseColumnLabel(tourInfoSort.column)}</span>{' '}
                  ({tourInfoSort.direction})
                </div>
                <button
                  type="button"
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-medium text-slate-200 hover:bg-white/10"
                  onClick={clearTourInfoFilters}
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        </div>

        {tourInfoError && <div className="text-sm text-red-700">{tourInfoError}</div>}

        <div className="rounded-3xl border border-slate-800 bg-slate-950/75 p-4 shadow-[0_24px_60px_rgba(15,23,42,0.28)]">
          <div className="mb-4 rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-cyan-50">Bulk Move Selected Tours</div>
                <div className="text-xs text-cyan-100/70">
                  Select tours, then move them into another canonical hub. Unbuilt canonical hubs are shown but disabled until you build activity hubs.
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className="rounded-xl border border-cyan-400/20 bg-slate-950 px-3 py-2 text-sm text-white"
                  value={tourInfoMoveTarget}
                  onChange={(e) => setTourInfoMoveTarget(e.target.value)}
                >
                  <option value="">Move selected tours to...</option>
                  {canonicalHubMoveTargets.map((hub) => (
                    <option
                      key={`tour-info-target-${hub.slug}`}
                      value={hub.areaId || ''}
                      disabled={!hub.isBuilt}
                    >
                      {hub.name} ({hub.slug}){hub.isBuilt ? '' : ' - not built yet'}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="rounded-xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={moveSelectedTourInfoRows}
                  disabled={tourInfoMoving || !selectedTourInfoRows.length || !tourInfoMoveTarget}
                >
                  {tourInfoMoving ? 'Moving...' : 'Move To Hub'}
                </button>
              </div>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-white">Column Filters</div>
              <div className="text-xs text-slate-400">
                Each column can be filtered independently. Small-cardinality columns use dropdown filters; everything else uses contains-match text filters.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {tourInfoDisplayColumns.map((column) => {
              const uniqueValues = tourInfoUniqueValuesByColumn[column] || [];
              const useSelect = uniqueValues.length > 0 && uniqueValues.length <= 12;
              return (
                <div key={`tour-filter-${column}`} className="rounded-2xl border border-slate-800 bg-slate-900/70 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {formatDatabaseColumnLabel(column)}
                  </div>
                  {useSelect ? (
                    <select
                      className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                      value={tourInfoFilters[column] || ''}
                      onChange={(e) => updateTourInfoFilter(column, e.target.value)}
                    >
                      <option value="">All values</option>
                      {uniqueValues.map((entry) => (
                        <option key={`${column}-${entry.value}`} value={entry.value}>
                          {entry.value} ({entry.count})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500"
                      placeholder={`Filter ${column}`}
                      value={tourInfoFilters[column] || ''}
                      onChange={(e) => updateTourInfoFilter(column, e.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-950/85 shadow-[0_24px_60px_rgba(15,23,42,0.32)]">
          <div className="max-h-[70vh] overflow-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur">
                <tr>
                  <th className="border-b border-slate-800 px-3 py-3 text-left align-bottom">
                    <label className="flex items-center gap-2 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
                      <input
                        type="checkbox"
                        checked={allVisibleTourInfoRowsSelected}
                        onChange={(e) => toggleAllVisibleTourInfoRows(e.target.checked)}
                      />
                      <span>Select</span>
                    </label>
                  </th>
                  {tourInfoDisplayColumns.map((column) => {
                    const isSorted = tourInfoSort.column === column;
                    return (
                      <th
                        key={`tour-col-${column}`}
                        className="border-b border-slate-800 px-3 py-3 text-left align-bottom"
                      >
                        <button
                          type="button"
                          className="flex items-center gap-2 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.16em] text-slate-300 hover:text-white"
                          onClick={() => toggleTourInfoSort(column)}
                        >
                          <span>{formatDatabaseColumnLabel(column)}</span>
                          <span className={`text-[10px] ${isSorted ? 'text-cyan-300' : 'text-slate-600'}`}>
                            {isSorted ? (tourInfoSort.direction === 'asc' ? '▲' : '▼') : '↕'}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {tourInfoLoading && !tourInfoRows.length ? (
                  <tr>
                    <td colSpan={Math.max(2, tourInfoDisplayColumns.length + 1)} className="px-4 py-8 text-center text-slate-400">
                      Loading tours...
                    </td>
                  </tr>
                ) : tourInfoDisplayRows.length ? (
                  tourInfoDisplayRows.map((row, index) => (
                    <tr
                      key={`tour-row-${row?.id || index}`}
                      className={index % 2 === 0 ? 'bg-slate-950/60' : 'bg-slate-900/60'}
                    >
                      <td className="border-b border-slate-900 px-3 py-2 align-top text-slate-200">
                        <input
                          type="checkbox"
                          checked={Boolean(tourInfoSelectedRows[String(row?.id || '')])}
                          onChange={(e) => toggleTourInfoRowSelected(row?.id, e.target.checked)}
                        />
                      </td>
                      {tourInfoDisplayColumns.map((column) => (
                        <td
                          key={`tour-row-${row?.id || index}-${column}`}
                          className="max-w-[320px] border-b border-slate-900 px-3 py-2 align-top text-slate-200"
                        >
                          <div className="break-words whitespace-pre-wrap">
                            {getDatabaseCellText(row?.[column]) || <span className="text-slate-600">-</span>}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={Math.max(2, tourInfoDisplayColumns.length + 1)} className="px-4 py-10 text-center text-slate-400">
                      No tours match the current search and column filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {activeSetupTab === 'Classifier Threshold & Scoring' && (
      <div className="space-y-2">
        <h3 className="font-semibold text-gray-100">Classifier Threshold & Scoring</h3>
        <p className="text-sm text-gray-300">
          The classifier matches tours into your existing canonical activity hubs first, then shows unmatched patterns as suggestions for taxonomy updates.
        </p>
        {classifierPreviewLoading ? <div className="text-xs text-gray-400">Refreshing classifier preview...</div> : null}
        {classifierPreviewError ? <div className="text-xs text-red-700">{classifierPreviewError}</div> : null}
        <div className="border rounded p-3 bg-black/20 space-y-2">
          <div className="text-sm font-medium text-gray-200">Canonical Activity Hub Slugs</div>
          <div className="text-xs text-gray-300">
            Use this dictionary to normalize messy auto-labels (for example bike/cycle/cycling -> cycling-tours).
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {hubTaxonomy.map((item, index) => (
              <div key={`taxonomy-${index}`} className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input
                  type="text"
                  className="border p-2 w-full"
                  placeholder="Slug"
                  value={item.slug}
                  onChange={(e) =>
                    setHubTaxonomy((prev) =>
                      prev.map((row, rowIndex) =>
                        rowIndex === index ? { ...row, slug: e.target.value } : row
                      )
                    )
                  }
                />
                <input
                  type="text"
                  className="border p-2 w-full"
                  placeholder="Display name"
                  value={item.name}
                  onChange={(e) =>
                    setHubTaxonomy((prev) =>
                      prev.map((row, rowIndex) =>
                        rowIndex === index ? { ...row, name: e.target.value } : row
                      )
                    )
                  }
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="border p-2 w-full"
                    placeholder="Synonyms (comma separated)"
                    value={item.synonyms}
                    onChange={(e) =>
                      setHubTaxonomy((prev) =>
                        prev.map((row, rowIndex) =>
                          rowIndex === index ? { ...row, synonyms: e.target.value } : row
                        )
                      )
                    }
                  />
                  <button
                    type="button"
                    className="px-3 py-2 rounded bg-red-700 text-white"
                    onClick={() =>
                      setHubTaxonomy((prev) => prev.filter((_row, rowIndex) => rowIndex !== index))
                    }
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="bg-gray-700 text-white px-4 py-2 rounded"
            onClick={() =>
              setHubTaxonomy((prev) => [...prev, { slug: '', name: '', synonyms: '' }])
            }
          >
            Add Canonical Hub
          </button>
        </div>
        <div className="border rounded p-3 bg-black/20 space-y-2">
          <div className="text-sm font-medium text-gray-200">Suggested Slugs (From Current Destination)</div>
          <div className="text-xs text-gray-300">
            These are new keyword patterns from the current classifier run that do not yet map to a canonical activity hub.
          </div>
          {!suggestedCanonicalSlugs.length ? (
            <div className="text-xs text-gray-400">No new suggestions right now.</div>
          ) : (
            <>
              <button
                type="button"
                className="bg-indigo-700 text-white px-3 py-2 rounded text-sm"
                onClick={addAllSuggestedSlugs}
              >
                Add All Suggested
              </button>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {suggestedCanonicalSlugs.map((item) => (
                  <div key={`suggested-${item.slug}`} className="border rounded p-2">
                    <div className="text-sm text-gray-100">
                      {item.name} <span className="text-gray-400">({item.slug})</span>
                    </div>
                    <div className="text-xs text-gray-300">
                      Activities: {item.activitiesCount} · Score: {item.score ?? 'n/a'}
                    </div>
                    <div className="text-xs text-gray-300 mt-1">
                      Classifier keywords: {item.debugKeywords?.length ? item.debugKeywords.join(', ') : 'n/a'}
                    </div>
                    <div className="text-xs text-gray-300 mt-1">
                      Source phrases: {item.debugSourcePhrases?.length ? item.debugSourcePhrases.join(', ') : 'n/a'}
                    </div>
                    <div className="text-xs text-gray-300 mt-1">
                      Canonical match: {item.debugCanonicalMatch?.slug || 'none'}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                      <input
                        type="text"
                        className="border p-2 w-full text-xs"
                        value={suggestedSlugDrafts[item.slug]?.slug ?? item.slug}
                        onChange={(e) =>
                          updateSuggestedSlugDraft(item.slug, { slug: e.target.value })
                        }
                        placeholder="Suggested slug"
                      />
                      <input
                        type="text"
                        className="border p-2 w-full text-xs"
                        value={suggestedSlugDrafts[item.slug]?.name ?? item.name}
                        onChange={(e) =>
                          updateSuggestedSlugDraft(item.slug, { name: e.target.value })
                        }
                        placeholder="Suggested name"
                      />
                      <input
                        type="text"
                        className="border p-2 w-full text-xs"
                        value={suggestedSlugDrafts[item.slug]?.synonyms ?? item.synonyms}
                        onChange={(e) =>
                          updateSuggestedSlugDraft(item.slug, { synonyms: e.target.value })
                        }
                        placeholder="Synonyms seed"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 mt-2">
                      <select
                        className="border p-2 w-full text-xs"
                        value={suggestedSlugDrafts[item.slug]?.targetCanonicalSlug ?? ''}
                        onChange={(e) =>
                          updateSuggestedSlugDraft(item.slug, {
                            targetCanonicalSlug: e.target.value,
                          })
                        }
                      >
                        <option value="">Choose existing canonical slug to merge words...</option>
                        {taxonomyOptions.map((option) => (
                          <option key={`suggestion-${item.slug}-target-${option.slug}`} value={option.slug}>
                            {option.name} ({option.slug})
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="px-3 py-1 rounded bg-emerald-700 text-white text-xs"
                        onClick={() => addSuggestedSlug(item)}
                      >
                        Add as New Canonical
                      </button>
                      <button
                        type="button"
                        className={`px-3 py-1 rounded text-white text-xs ${
                          suggestedSlugDrafts[item.slug]?.targetCanonicalSlug
                            ? 'bg-blue-700'
                            : 'bg-gray-600 cursor-not-allowed'
                        }`}
                        onClick={() => mergeSuggestedIntoCanonical(item)}
                        disabled={!suggestedSlugDrafts[item.slug]?.targetCanonicalSlug}
                      >
                        Merge Words Into Selected
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="border rounded p-3 bg-black/20 space-y-2">
          <div className="text-sm font-medium text-gray-200">Proposed Activity Hubs</div>
          <div className="text-xs text-gray-300">
            Canonical hubs populated by the current classifier run. They are filtered by the threshold below and ordered by popularity.
          </div>
          {!projectedCanonicalHubs.length ? (
            <div className="text-xs text-gray-400">
              No canonical hubs meet the current threshold of {projectedThreshold} activities.
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {projectedCanonicalHubs.map((hub) => (
                <div key={`projected-${hub.slug}`} className="border rounded p-2">
                  <div className="text-sm text-gray-100">
                    {hub.name} <span className="text-gray-400">({hub.slug})</span>
                  </div>
                  <div className="text-xs text-gray-300">
                    Activities: {hub.activitiesCount} · Score: {hub.score ?? 'n/a'}
                  </div>
                  <div className="text-xs text-gray-300 mt-1">
                    Source phrases: {hub.sourcePhrases?.length ? hub.sourcePhrases.join(', ') : 'n/a'}
                  </div>
                  <div className="text-xs text-gray-300 mt-2">Top tours:</div>
                  <ul className="text-xs text-gray-300 list-disc ml-5 mt-1">
                    {(hub.tours || []).slice(0, 5).map((tour) => (
                      <li key={`projected-${hub.slug}-${tour.tourId || tour.tourTitle}`}>{tour.tourTitle || tour.tourId}</li>
                    ))}
                    {(!hub.tours || hub.tours.length === 0) && <li>No tours matched.</li>}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border rounded p-3 bg-black/20 space-y-2">
          <div className="text-sm font-medium text-gray-200">Global Minimum Activities</div>
          <div className="text-xs text-gray-300">
            Global default: <strong>{globalThreshold}</strong> activities. Lower or raise this to preview which canonical hubs survive.
          </div>
          <div className="flex flex-col md:flex-row gap-2 md:items-end">
            <div className="flex-1">
              <input
                type="number"
                min="1"
                className="border p-2 w-full"
                value={globalThresholdDraft}
                onChange={(e) => setGlobalThresholdDraft(e.target.value)}
                disabled={globalLoading}
              />
            </div>
            <button
              type="button"
              className="bg-blue-600 text-white px-4 py-2 rounded"
              onClick={saveGlobalThreshold}
              disabled={globalSaving || globalLoading}
            >
              {globalSaving ? 'Saving…' : 'Save Threshold & Canonical Hubs'}
            </button>
          </div>
        </div>
        {globalError && <div className="text-sm text-red-700">{globalError}</div>}
        {globalMessage && <div className="text-sm text-green-700">{globalMessage}</div>}
      </div>
      )}

      {activeSetupTab === 'AI Agents' && (
      <div className="space-y-2">
        <h3 className="font-semibold text-gray-100">AI Agents</h3>
        <p className="text-sm text-gray-300">
          Configure Nina, Kryten, Poly, Gordon, Renee, Chris, and Webster separately so writing, humanizing, translation, menu OCR, campaign generation, artwork, and website copy can each use the provider and model that fit best.
        </p>
        {agentSettingsError ? (
          <div className="text-sm text-rose-300">{agentSettingsError}</div>
        ) : agentSettingsMessage ? (
          <div className="text-sm text-emerald-300">{agentSettingsMessage}</div>
        ) : (
          <div className="text-xs text-slate-400">
            {agentSettingsSaving
              ? 'Saving selected AI agent models...'
              : agentSettingsDirty
                ? 'You have unsaved AI agent changes.'
                : 'Change providers and models, then click Save All.'}
          </div>
        )}
        <div className="relative overflow-hidden rounded-[2rem] border border-cyan-200/10 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.2),_transparent_32%),radial-gradient(circle_at_top_right,_rgba(34,211,238,0.2),_transparent_34%),linear-gradient(145deg,rgba(15,23,42,0.95),rgba(17,24,39,0.9))] p-5 md:p-6">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-200/80">AI Crew</div>
              <h4 className="mt-2 text-xl font-semibold text-white">Meet the voices behind your activity hubs</h4>
              <p className="mt-2 max-w-3xl text-sm text-slate-300">
                Nina writes the first bright, conversion-friendly pass. Kryten follows with a more polished humanizer pass when a hub needs an extra editorial sweep. Poly handles translation settings so multilingual tools can share one configured voice and model choice. Gordon is the menu OCR specialist for turning photographed menu pages into clean Menu QR entries. Renee owns campaign copy generation, Chris takes that campaign output forward into artwork creation, and Webster is the venue website copywriter who turns review summary and marketing kit data into polished English sections. These settings are saved once here and shared across the webserver for every user.
              </p>
            </div>
            <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300">
              One master config for all users
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <div className="text-xs text-slate-300">
              {agentSettingsDirty ? 'Unsaved changes are ready to save.' : 'All AI agent settings are saved.'}
            </div>
            <button
              type="button"
              className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-950 shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
              onClick={saveAgentSettings}
              disabled={agentSettingsSaving || !agentSettingsDirty}
            >
              {agentSettingsSaving ? 'Saving…' : 'Save All'}
            </button>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className={AGENT_CARD_BASE_CLASS}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,214,10,0.18),_transparent_38%),radial-gradient(circle_at_bottom_right,_rgba(251,113,133,0.16),_transparent_34%)]" />
              <div className="relative">
                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                  <img
                    src={ninaAvatar}
                    alt="Illustrated avatar of Nina, Suitestream.tv's marketer"
                    className="h-32 w-32 shrink-0 rounded-[1.5rem] border border-white/10 bg-white/10 object-cover shadow-lg shadow-amber-500/10"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="rounded-full bg-amber-300/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-100">
                        Marketer
                      </div>
                      <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-slate-300">
                        Bright, bubbly, always on
                      </div>
                    </div>
                    <h5 className="mt-3 text-2xl font-semibold text-white">Meet Nina</h5>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm leading-6 text-slate-200">
                      Nina is your enthusiastic marketer: a bright and bubbly 23-year-old girl next door with frizzy hair, full of upbeat energy and ready to turn raw hub data into inviting first-draft copy.
                    </p>
                  </div>
                  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-amber-100/80">
                    Best for first-pass positioning, selling points and warm intros
                  </p>
                </div>
              </div>

              <div className="relative mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-200">Provider</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={ninaProvider}
                    onChange={(e) => handleNinaProviderChange(e.target.value)}
                  >
                    <option value="gemini">Gemini</option>
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-200">Model</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={ninaModel}
                    onChange={(e) => setNinaModel(e.target.value)}
                    disabled={ninaModelsLoading && ninaProviderModels.length === 0}
                  >
                    {ninaProviderModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                  {ninaModelsLoading ? <div className="mt-1 text-xs text-slate-400">Refreshing models from API...</div> : null}
                </div>
              </div>

              <div className="relative mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 text-xs leading-5 text-slate-300">
                Nina stays configurable here, but bulk-send has been removed from this panel. Use the per-hub controls below when you want to generate content deliberately.
              </div>

              {ninaModelsError ? <div className="relative mt-3 text-xs text-amber-200">{ninaModelsError}</div> : null}
              {ninaMessage ? <div className="relative mt-2 text-sm text-emerald-300">{ninaMessage}</div> : null}
              {ninaError ? <div className="relative mt-2 text-sm text-rose-300">{ninaError}</div> : null}
            </div>

            <div className={AGENT_CARD_BASE_CLASS}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.16),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(148,163,184,0.18),_transparent_32%)]" />
              <div className="relative">
                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                  <img
                    src={krytenAvatar}
                    alt="Illustrated avatar of Kryten, Suitestream.tv's humanizer robot"
                    className="h-32 w-32 shrink-0 rounded-[1.5rem] border border-white/10 bg-slate-900/60 object-cover shadow-lg shadow-cyan-500/10"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="rounded-full bg-cyan-300/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-100">
                        Humanizer
                      </div>
                      <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-slate-300">
                        Younger, sharper, more functional
                      </div>
                    </div>
                    <h5 className="mt-3 text-2xl font-semibold text-white">Kryten</h5>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm leading-6 text-slate-200">
                    Kryten is a younger, more capable take on the classic Red Dwarf-style service robot: polished, efficient and slightly dry, built to humanize Nina&apos;s output into something smoother and more natural.
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-cyan-100/80">
                    Best for refinement, cleanup and final human-sounding polish
                  </p>
                </div>
              </div>

              <div className="relative mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-200">Provider</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={krytenProvider}
                    onChange={(e) => setKrytenProvider(normalizeAgentProvider(e.target.value))}
                  >
                    <option value="gemini">Gemini</option>
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-200">Model</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={krytenModel}
                    onChange={(e) => setKrytenModel(e.target.value)}
                    disabled={ninaModelsLoading && krytenProviderModels.length === 0}
                  >
                    {krytenProviderModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                  {ninaModelsLoading ? <div className="mt-1 text-xs text-slate-400">Refreshing models from API...</div> : null}
                </div>
              </div>

              <div className="relative mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 text-xs leading-5 text-slate-300">
                Configure Kryten separately from Nina so humanizer runs can be tuned on their own provider and model without changing the first-pass copy setup.
              </div>

              {ninaModelsError ? <div className="relative mt-3 text-xs text-amber-200">{ninaModelsError}</div> : null}
            </div>

            <div className={AGENT_CARD_BASE_CLASS}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(167,243,208,0.18),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(96,165,250,0.18),_transparent_34%)]" />
              <div className="relative">
                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                  <img
                    src={polyAvatar}
                    alt="Illustrated avatar of Poly, Suitestream.tv's translation specialist"
                    className="h-32 w-32 shrink-0 rounded-[1.5rem] border border-white/10 bg-emerald-400/10 object-cover shadow-lg shadow-emerald-500/10"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="rounded-full bg-emerald-300/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-100">
                        Polyglot
                      </div>
                      <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-slate-300">
                        Translation control center
                      </div>
                    </div>
                    <h5 className="mt-3 text-2xl font-semibold text-white">Poly</h5>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm leading-6 text-slate-200">
                    Poly is the multilingual specialist. Her provider and model settings will be used by translation features as they move onto the shared translation workflow, starting with plaques and then menus.
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-emerald-100/80">
                    Best for multilingual copy, translation quality checks and future shared language tools
                  </p>
                </div>
              </div>

              <div className="relative mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-200">Provider</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={polyProvider}
                    onChange={(e) => setPolyProvider(normalizeAgentProvider(e.target.value))}
                  >
                    <option value="gemini">Gemini</option>
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-200">Model</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={polyModel}
                    onChange={(e) => setPolyModel(e.target.value)}
                    disabled={ninaModelsLoading && polyProviderModels.length === 0}
                  >
                    {polyProviderModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                  {ninaModelsLoading ? <div className="mt-1 text-xs text-slate-400">Refreshing models from API...</div> : null}
                </div>
              </div>

              <div className="relative mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 text-xs leading-5 text-slate-300">
                Poly is configuration-only in this phase. Translation screens will adopt her saved provider and model as the shared translation workflow is rolled out.
              </div>

              {ninaModelsError ? <div className="relative mt-3 text-xs text-amber-200">{ninaModelsError}</div> : null}
            </div>

            <div className={AGENT_CARD_BASE_CLASS}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.2),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(248,113,113,0.18),_transparent_34%)]" />
              <div className="relative">
                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                  <img
                    src={gordonAvatar}
                    alt="Illustrated avatar of Gordon, Suitestream.tv's menu OCR chef"
                    className="h-32 w-32 shrink-0 rounded-[1.5rem] border border-white/10 bg-amber-400/10 object-cover shadow-lg shadow-amber-500/10"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="rounded-full bg-amber-300/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-100">
                        OCR Chef
                      </div>
                      <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-slate-300">
                        Menu parser, no nonsense
                      </div>
                    </div>
                    <h5 className="mt-3 text-2xl font-semibold text-white">Gordon</h5>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm leading-6 text-slate-200">
                    Gordon is the menu extraction specialist, inspired by Gordon Ramsay. He reads menu page images, pasted text, CSV-style menu exports and messy copied website content, then pulls out section names, item text, subtitles, descriptions and prices in a structure that can feed Menu QR entries.
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-amber-100/80">
                    Best for menu OCR, pasted menu text, CSV imports and structured extraction
                  </p>
                </div>
              </div>

              <div className="relative mt-5 space-y-4">
                <div className="rounded-2xl border border-white/10 bg-black/10 p-4">
                  <div className="mb-3">
                    <div className="text-sm font-semibold text-white">Image Import Model</div>
                    <div className="text-xs text-slate-400">Used for photographed or scanned menu pages.</div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-200">Provider</label>
                      <select
                        className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                        value={gordonProvider}
                        onChange={(e) => setGordonProvider(normalizeAgentProvider(e.target.value))}
                      >
                        <option value="gemini">Gemini</option>
                        <option value="openai">OpenAI</option>
                        <option value="anthropic">Anthropic</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-slate-200">Model</label>
                      <select
                        className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                        value={gordonModel}
                        onChange={(e) => setGordonModel(e.target.value)}
                        disabled={ninaModelsLoading && gordonProviderModels.length === 0}
                      >
                        {gordonProviderModels.map((model) => (
                          <option key={model} value={model}>
                            {model}
                          </option>
                        ))}
                      </select>
                      {ninaModelsLoading ? <div className="mt-1 text-xs text-slate-400">Refreshing models from API...</div> : null}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-black/10 p-4">
                  <div className="mb-3">
                    <div className="text-sm font-semibold text-white">Text Import Model</div>
                    <div className="text-xs text-slate-400">Used for menu text imports and requested edits to saved menus.</div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-200">Provider</label>
                      <select
                        className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                        value={gordonTextProvider}
                        onChange={(e) => setGordonTextProvider(normalizeAgentProvider(e.target.value))}
                      >
                        <option value="anthropic">Anthropic</option>
                        <option value="gemini">Gemini</option>
                        <option value="openai">OpenAI</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-slate-200">Model</label>
                      <select
                        className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                        value={gordonTextModel}
                        onChange={(e) => setGordonTextModel(e.target.value)}
                        disabled={ninaModelsLoading && gordonTextProviderModels.length === 0}
                      >
                        {gordonTextProviderModels.map((model) => (
                          <option key={model} value={model}>
                            {model}
                          </option>
                        ))}
                      </select>
                      {ninaModelsLoading ? <div className="mt-1 text-xs text-slate-400">Refreshing models from API...</div> : null}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-black/10 p-4">
                  <div className="mb-3">
                    <div className="text-sm font-semibold text-white">Menu Photo Identification</div>
                    <div className="text-xs text-slate-400">Used independently to identify S3 food and drink photos and match them to saved menu items. Default: GPT-6 Astra.</div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-200">Provider</label>
                      <select
                        className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                        value={gordonPhotoProvider}
                        onChange={(e) => {
                          const provider = normalizeAgentProvider(e.target.value);
                          setGordonPhotoProvider(provider);
                          setGordonPhotoModel(provider === 'openai' ? DEFAULT_GORDON_PHOTO_MODEL : provider === 'gemini' ? 'gemini-2.5-flash' : 'claude-sonnet-4-20250514');
                        }}
                      >
                        <option value="anthropic">Anthropic</option>
                        <option value="gemini">Gemini</option>
                        <option value="openai">OpenAI</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-slate-200">Model</label>
                      <select
                        className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                        value={gordonPhotoModel}
                        onChange={(e) => setGordonPhotoModel(e.target.value)}
                        disabled={ninaModelsLoading && gordonPhotoProviderModels.length === 0}
                      >
                        {gordonPhotoProviderModels.map((model) => (
                          <option key={model} value={model}>
                            {model}
                          </option>
                        ))}
                      </select>
                      {ninaModelsLoading ? <div className="mt-1 text-xs text-slate-400">Refreshing models from API...</div> : null}
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 text-xs leading-5 text-slate-300">
                Menu extraction, saved-menu edits, and photo identification run separately. Upload photos whenever they become available, then ask Gordon to organize them.
              </div>

              {ninaModelsError ? <div className="relative mt-3 text-xs text-amber-200">{ninaModelsError}</div> : null}
            </div>

            <div className={AGENT_CARD_BASE_CLASS}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(244,114,182,0.18),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(251,191,36,0.18),_transparent_34%)]" />
              <div className="relative">
                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                  <img
                    src={reneeAvatar}
                    alt="Illustrated avatar of Renee, Suitestream.tv's festival campaign organiser"
                    className="h-32 w-32 shrink-0 rounded-[1.5rem] border border-white/10 bg-rose-400/10 object-cover shadow-lg shadow-rose-500/10"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="rounded-full bg-rose-300/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-rose-100">
                        Campaign Organiser
                      </div>
                      <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-slate-300">
                        Big festivals, tight deadlines, clear briefs
                      </div>
                    </div>
                    <h5 className="mt-3 text-2xl font-semibold text-white">Renee</h5>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm leading-6 text-slate-200">
                    Renee is a Kiwi organiser of very big festivals: charismatic, practical and calm under pressure. She takes a rough campaign brief and turns it into the structured copy pack that seeds a new campaign in Creative Studio.
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-rose-100/80">
                    Best for single-prompt campaign creation, launch copy and campaign structure
                  </p>
                </div>
              </div>

              <div className="relative mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-200">Provider</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={reneeProvider}
                    onChange={(e) => setReneeProvider(normalizeAgentProvider(e.target.value))}
                  >
                    <option value="gemini">Gemini</option>
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-200">Model</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={reneeModel}
                    onChange={(e) => setReneeModel(e.target.value)}
                    disabled={ninaModelsLoading && reneeProviderModels.length === 0}
                  >
                    {reneeProviderModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                  {ninaModelsLoading ? <div className="mt-1 text-xs text-slate-400">Refreshing models from API...</div> : null}
                </div>
              </div>

              <div className="relative mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 text-xs leading-5 text-slate-300">
                Renee now owns the prompt-to-campaign copy workflow in Creative Studio. Her saved provider and model will be used when a campaign is created from a single prompt.
              </div>

              {ninaModelsError ? <div className="relative mt-3 text-xs text-amber-200">{ninaModelsError}</div> : null}
            </div>

            <div className={AGENT_CARD_BASE_CLASS}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.18),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(34,197,94,0.18),_transparent_34%)]" />
              <div className="relative">
                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                  <img
                    src={chrisAvatar}
                    alt="Illustrated avatar of Chris, Suitestream.tv's social media artwork guru"
                    className="h-32 w-32 shrink-0 rounded-[1.5rem] border border-white/10 bg-sky-400/10 object-cover shadow-lg shadow-sky-500/10"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="rounded-full bg-sky-300/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-100">
                        Artwork Guru
                      </div>
                      <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-slate-300">
                        Charismatic, honest, down to earth
                      </div>
                    </div>
                    <h5 className="mt-3 text-2xl font-semibold text-white">Chris</h5>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm leading-6 text-slate-200">
                    Chris is inspired by Chris Love: a suave but honest social media operator who knows how to turn campaign copy into visual direction that actually lands. He owns AI artwork generation for campaigns without changing the copy-generation setup.
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-sky-100/80">
                    Best for campaign artwork, promo visuals and refining generated image direction
                  </p>
                </div>
              </div>

              <div className="relative mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-200">Provider</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={chrisProvider}
                    onChange={(e) => setChrisProvider(normalizeAgentImageProvider(e.target.value))}
                  >
                    <option value="gemini">Gemini</option>
                    <option value="openai">OpenAI</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-200">Model</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={chrisModel}
                    onChange={(e) => setChrisModel(e.target.value)}
                  >
                    {chrisProviderModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="relative mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 text-xs leading-5 text-slate-300">
                Chris now owns Creative Studio AI artwork generation. Image creation and image-edit requests will use his saved provider and model.
              </div>
            </div>

            <div className={AGENT_CARD_BASE_CLASS}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.18),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.18),_transparent_34%)]" />
              <div className="relative">
                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                  <img
                    src={ninaAvatar}
                    alt="Illustrated avatar of Webster, Suitestream.tv's English website builder"
                    className="h-32 w-32 shrink-0 rounded-[1.5rem] border border-white/10 bg-amber-400/10 object-cover shadow-lg shadow-amber-500/10"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="rounded-full bg-amber-300/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-100">
                        Website Builder
                      </div>
                      <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-slate-300">
                        English-first, section-aware, JSON only
                      </div>
                    </div>
                    <h5 className="mt-3 text-2xl font-semibold text-white">Webster</h5>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm leading-6 text-slate-200">
                    Webster is the English website copywriter for venue pages. He takes the review summary, marketing kit, destination context, and requested website section keys, then returns clean structured JSON that can drop straight into template sections.
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-amber-100/80">
                    Best for homepage blocks, venue intros, about copy and future website section generation
                  </p>
                </div>
              </div>

              <div className="relative mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-200">Provider</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={websterProvider}
                    onChange={(e) => setWebsterProvider(normalizeAgentProvider(e.target.value))}
                  >
                    <option value="gemini">Gemini</option>
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-200">Model</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-slate-900/90 p-2 text-slate-100"
                    value={websterModel}
                    onChange={(e) => setWebsterModel(e.target.value)}
                    disabled={ninaModelsLoading && websterProviderModels.length === 0}
                  >
                    {websterProviderModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                  {ninaModelsLoading ? <div className="mt-1 text-xs text-slate-400">Refreshing models from API...</div> : null}
                </div>
              </div>

              <div className="relative mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 text-xs leading-5 text-slate-300">
                Webster is configuration-ready in this step. Website build flow will call him next to generate structured section copy, and his output must remain JSON-only.
              </div>

              {ninaModelsError ? <div className="relative mt-3 text-xs text-amber-200">{ninaModelsError}</div> : null}
            </div>
          </div>
        </div>
      </div>
      )}

      {activeSetupTab === 'Activity Hubs (Manual Controls)' && (
      <div className="space-y-2">
        <h3 className="font-semibold text-gray-100">Activity Hubs (Manual Controls)</h3>
        <p className="text-sm text-gray-300">
          Build canonical hubs after reviewing the classifier suggestions, then fine-tune titles/slugs and manually force include/exclude for specific tours.
        </p>
        <button
          type="button"
          className="bg-emerald-700 text-white px-4 py-2 rounded"
          onClick={buildActivityHubs}
          disabled={buildRunning || buildEligibleCanonicalHubs.length === 0}
        >
          {buildRunning ? 'Building Activity Hubs...' : `Build Activity Hubs${buildEligibleCanonicalHubs.length ? ` (${buildEligibleCanonicalHubs.length})` : ''}`}
        </button>
        {!buildEligibleCanonicalHubs.length && (
          <div className="text-xs text-gray-400">
            No canonical hubs are ready to build yet. Review Suggested Slugs, update the canonical taxonomy, save it, then rerun the classifier.
          </div>
        )}
        {hubsLoading ? <div className="text-sm text-gray-600">Loading hubs...</div> : null}
        {hubsError && <div className="text-sm text-red-700">{hubsError}</div>}
        {krytenMessage ? <div className="text-sm text-green-700">{krytenMessage}</div> : null}
        {krytenError ? <div className="text-sm text-red-700">{krytenError}</div> : null}
        {!hubsLoading && hubs.length === 0 && (
          <div className="text-sm text-gray-500">No built hubs yet. Run the classifier, review suggestions, then build the canonical hubs here.</div>
        )}
        {!hubsLoading && hubs.length > 0 && enabledHubs.length === 0 && (
          <div className="text-sm text-gray-500">All built hubs are currently disabled.</div>
        )}
        {enabledHubs.map((hub) => {
          const key = String(hub.areaId);
          const draft = hubDrafts[key] || { name: hub.name || '', slug: hub.slug || '' };
          const widgetDraft = hubWidgetDrafts[key] || {
            query: `${site?.name || ''} ${hub?.name || ''}`.trim(),
            localeCode: 'en-US',
            numberOfItems: '20',
            campaignId: `SCV-${site?.id || ''}-${toSlugSegment(site?.destination_slug || destinationSlug || site?.name || '')}-${toSlugSegment(hub?.slug || '')}`.slice(0, 120),
            partnerId: 'BEWYOUX',
            updateExisting: false,
          };
          const ninaStatus = String(hub.ninaStatus || 'not_started');
          const krytenStatus = krytenHumanizingHubId === hub.areaId
            ? 'generating'
            : String(hub.krytenStatus || 'not_started');
          const ninaContent = hub.ninaContent;
          const ninaRawText = String(hub.ninaRawText || '').trim();
          const ninaErrorText = String(hub.ninaError || '').trim();
          const krytenErrorText = String(hub.krytenError || '').trim();
          const publishedContent = hub.publishedContent;
          const plaquePath = hub.plaqueLandingKey ? `/lp/p/${hub.plaqueLandingKey}` : '';
          const published = isHubPublished(hub);
          const hubExpanded = isHubCardExpanded(hub);
          const publishedTourTitles = extractPublishedHubTourTitles(hub);
          const canPublish = Boolean(hub.currentOutputRevisionId && (ninaStatus === 'complete' || krytenStatus === 'complete'));
          const canHumanize = Boolean(hub.ninaRevisionId && ninaStatus === 'complete');
          const ninaStatusClassName = getAgentStatusClassName(ninaStatus);
          const krytenStatusClassName = getAgentStatusClassName(krytenStatus);
          const ninaLifecycleText =
            hub.ninaRevisionId && hub.publishedRevisionId === hub.ninaRevisionId
              ? `Lifecycle: published · published rev ${hub.publishedRevisionId}`
              : `Lifecycle: ${hub.hubLifecycleStatus || 'draft'} · not published`;
          const krytenLifecycleText =
            hub.krytenRevisionId && hub.publishedRevisionId === hub.krytenRevisionId
              ? `Lifecycle: published · published rev ${hub.publishedRevisionId}`
              : `Lifecycle: ${hub.hubLifecycleStatus || 'draft'} · not published`;
          const moveTargets = enabledHubs.filter((candidate) => candidate?.areaId !== hub.areaId);
          const overrideMeta = hubTourOverrideMeta[key] || {
            allTours: [],
            filteredTours: [],
            locationOptions: [],
            query: '',
            location: '',
            sortBy: 'reviews_desc',
          };
          return (
            <div key={hub.areaId} className="space-y-2 rounded-2xl border border-white/10 bg-slate-950/40 p-3">
              <div
                className={`rounded-2xl border p-4 ${
                  published
                    ? 'border-blue-300/40 bg-blue-600/30'
                    : 'border-emerald-300/40 bg-emerald-600/30'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xl font-semibold text-white md:text-2xl">
                      {draft.name || hub.name || 'Untitled Hub'}
                    </div>
                    <div className="mt-1 text-xs text-blue-50/90">
                      {published ? 'Published landing page created' : 'Not published yet'}
                    </div>
                    <div className="mt-1 text-xs text-white/85">
                      Slug: <span className="font-mono text-gray-200">{draft.slug || hub.slug || 'n/a'}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {plaquePath ? (
                      <a
                        href={plaquePath}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-lg bg-blue-900 px-3 py-2 text-sm font-medium text-white"
                      >
                        View Landing Page
                      </a>
                    ) : null}
                    <button
                      type="button"
                      className="rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white"
                      onClick={() => toggleHubCardExpanded(hub)}
                    >
                      {hubExpanded ? 'Collapse' : 'Expand'}
                    </button>
                    <label className="flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-xs text-white">
                      <span>{hubToggleSavingId === hub.areaId ? 'Saving...' : 'Enabled'}</span>
                      <input
                        type="checkbox"
                        checked={hub.isEnabled !== false}
                        disabled={hubToggleSavingId === hub.areaId}
                        onChange={(e) => setHubEnabled(hub, e.target.checked)}
                      />
                    </label>
                  </div>
                </div>
                {!hubExpanded && published && (
                  <div className="mt-4 rounded-xl border border-blue-200/15 bg-slate-950/45 p-3">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-100/80">
                      Tours included in this Activity Hub:
                    </div>
                    <div className="mt-2 max-h-48 overflow-y-auto">
                      {publishedTourTitles.length ? (
                        <ul className="space-y-1 text-sm text-slate-100">
                          {publishedTourTitles.map((title, index) => (
                            <li key={`${hub.areaId}-published-tour-${index}`}>{title}</li>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-sm text-slate-300">No published tours were detected yet.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {hubExpanded && (
                <>
              <div className="border rounded p-2 bg-black/20">
                <div>
                  <div className="text-xs text-gray-300">
                    Status: <strong>{published ? 'Published' : 'Draft'}</strong>
                  </div>
                </div>
              </div>
              <div className="border rounded p-2 bg-black/20">
                <button
                  type="button"
                  className="w-full flex items-center justify-between text-xs font-medium text-gray-200"
                  onClick={() => toggleHubPanel(hub.areaId, 'edit-slug')}
                >
                  <span>Edit Slug Options</span>
                  <span>{isHubPanelOpen(hub.areaId, 'edit-slug') ? 'Hide' : 'Show'}</span>
                </button>
                {isHubPanelOpen(hub.areaId, 'edit-slug') && (
                  <div className="space-y-2 mt-2">
                    <input
                      type="text"
                      className="border p-2 w-full"
                      value={draft.name}
                      onChange={(e) =>
                        setHubDrafts((prev) => ({ ...prev, [key]: { ...draft, name: e.target.value } }))
                      }
                      placeholder="Hub name"
                    />
                    <input
                      type="text"
                      className="border p-2 w-full"
                      value={draft.slug}
                      onChange={(e) =>
                        setHubDrafts((prev) => ({ ...prev, [key]: { ...draft, slug: e.target.value } }))
                      }
                      placeholder="Hub slug"
                    />
                    <select
                      className="border p-2 w-full"
                      value=""
                      onChange={(e) => {
                        const selectedSlug = String(e.target.value || '').trim();
                        if (!selectedSlug) return;
                        const selected = taxonomyOptions.find((option) => option.slug === selectedSlug);
                        if (!selected) return;
                        setHubDrafts((prev) => ({
                          ...prev,
                          [key]: {
                            ...draft,
                            slug: selected.slug,
                            name:
                              String(draft.name || '').trim() === String(hub.name || '').trim()
                                ? selected.name
                                : draft.name,
                          },
                        }));
                      }}
                    >
                      <option value="">Map to canonical slug...</option>
                      {taxonomyOptions.map((option) => (
                        <option key={`${hub.areaId}-canonical-${option.slug}`} value={option.slug}>
                          {option.name} ({option.slug})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className={`px-3 py-2 rounded text-white ${isHubDirty(hub) ? 'bg-green-700' : 'bg-gray-500 cursor-not-allowed'}`}
                      onClick={() => saveHub(hub)}
                      disabled={hubSavingId === hub.areaId || !isHubDirty(hub)}
                    >
                      {hubSavingId === hub.areaId ? 'Saving...' : 'Save Hub Name/Slug'}
                    </button>
                    {!isHubDirty(hub) && (
                      <div className="text-xs text-gray-300">
                        Save is enabled only after you change this hub's name or slug.
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="text-sm text-gray-200">
                Score: <strong>{hub.score ?? 'n/a'}</strong> · Activities: <strong>{hub.activitiesCount ?? 0}</strong>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-block text-[11px] px-2 py-1 rounded ${ninaStatusClassName}`}>
                  Nina: {ninaStatus}
                </span>
                <span className="text-xs text-gray-300">
                  {formatAgentRunSummary({
                    provider: hub.ninaProvider,
                    model: hub.ninaModel,
                    generatedAt: hub.ninaGeneratedAt,
                    revisionId: hub.ninaRevisionId,
                  })}
                </span>
                <span className="text-xs text-gray-300">
                  {ninaLifecycleText}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-block text-[11px] px-2 py-1 rounded ${krytenStatusClassName}`}>
                  Kryten: {krytenStatus}
                </span>
                <span className="text-xs text-gray-300">
                  {formatAgentRunSummary({
                    provider: hub.krytenProvider,
                    model: hub.krytenModel,
                    generatedAt: hub.krytenGeneratedAt,
                    revisionId: hub.krytenRevisionId,
                  })}
                </span>
                <span className="text-xs text-gray-300">
                  {krytenLifecycleText}
                </span>
              </div>

              <div className="border rounded p-2 bg-black/20">
                <button
                  type="button"
                  className="w-full flex items-center justify-between text-xs font-medium text-gray-200"
                  onClick={() => toggleHubPanel(hub.areaId, 'nina')}
                >
                  <span>Nina Content</span>
                  <span>{isHubPanelOpen(hub.areaId, 'nina') ? 'Hide' : 'Show'}</span>
                </button>
                {isHubPanelOpen(hub.areaId, 'nina') && (
                  <>
                    {ninaStatus === 'not_started' && (
                      <div className="text-xs text-gray-400 mt-1">
                        Not generated yet. Use <strong>Send to Nina</strong> for this hub.
                      </div>
                    )}
                    {ninaStatus === 'generating' && (
                      <div className="text-xs text-yellow-300 mt-1">Generating content...</div>
                    )}
                    {ninaStatus === 'error' && (
                      <div className="text-xs text-red-300 mt-1">{ninaErrorText || 'Generation failed.'}</div>
                    )}
                    {ninaStatus === 'complete' && (
                      <>
                        {hub.currentOutputAgent === 'kryten' ? (
                          <div className="text-xs text-cyan-300 mt-1">
                            Current output has been humanized by Kryten and will be used for publish.
                          </div>
                        ) : null}
                        <pre className="text-xs mt-1 bg-black/30 p-2 rounded overflow-x-auto whitespace-pre-wrap">
                          {ninaContent ? JSON.stringify(ninaContent, null, 2) : ninaRawText || '(No content returned)'}
                        </pre>
                      </>
                    )}
                    {krytenStatus === 'error' ? (
                      <div className="text-xs text-red-300 mt-1">
                        Kryten error: {krytenErrorText || 'Humanization failed.'}
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              <div className="border rounded p-2 bg-black/20">
                <button
                  type="button"
                  className="w-full flex items-center justify-between text-xs font-medium text-gray-200"
                  onClick={() => toggleHubPanel(hub.areaId, 'published')}
                >
                  <span>Published Hub Content</span>
                  <span>{isHubPanelOpen(hub.areaId, 'published') ? 'Hide' : 'Show'}</span>
                </button>
                {isHubPanelOpen(hub.areaId, 'published') &&
                  (!hub.publishedRevisionId ? (
                    <div className="text-xs text-gray-400 mt-1">Nothing published yet.</div>
                  ) : (
                    <>
                      <div className="text-xs text-gray-300 mt-1">
                        Stored as an Info Plaque.
                        {plaquePath ? (
                          <>
                            {' '}
                            Landing path:{' '}
                            <a
                              href={plaquePath}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-400 underline"
                            >
                              {plaquePath}
                            </a>
                          </>
                        ) : null}
                      </div>
                      <pre className="text-xs mt-1 bg-black/30 p-2 rounded overflow-x-auto whitespace-pre-wrap">
                        {publishedContent ? JSON.stringify(publishedContent, null, 2) : '(Published revision has no JSON output)'}
                      </pre>
                    </>
                  ))}
              </div>

              <div className="border rounded p-2 bg-black/20">
                <div className="text-xs font-medium text-gray-200">Why This Hub Exists</div>
                <div className="text-xs text-gray-300 mt-1">
                  Classifier keywords: {(hub.debugKeywords || []).length ? hub.debugKeywords.join(', ') : 'n/a'}
                </div>
                <div className="text-xs text-gray-300 mt-1">
                  Source phrases: {(hub.debugSourcePhrases || []).length ? hub.debugSourcePhrases.join(', ') : 'n/a'}
                </div>
                <div className="text-xs text-gray-300 mt-1">
                  Canonical match: {hub.debugCanonicalMatch?.slug || 'none'}
                  {hub.debugCanonicalMatch?.score != null ? ` (score ${hub.debugCanonicalMatch.score})` : ''}
                </div>
                <div className="text-xs text-gray-300 mt-1">
                  Top matched tours:
                </div>
                <ul className="text-xs text-gray-300 list-disc ml-5 mt-1">
                  {(hub.debugTopMatchedTours || []).slice(0, 5).map((title, index) => (
                    <li key={`${hub.areaId}-debug-tour-${index}`}>{title}</li>
                  ))}
                  {(!hub.debugTopMatchedTours || hub.debugTopMatchedTours.length === 0) && (
                    <li>No matched tours recorded.</li>
                  )}
                </ul>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="px-3 py-2 rounded text-white bg-indigo-700"
                  onClick={() => sendSingleHubToNina(hub)}
                  disabled={ninaSendingAll || ninaSendingHubId === hub.areaId || krytenHumanizingHubId === hub.areaId}
                >
                  {ninaSendingHubId === hub.areaId ? 'Sending to Nina...' : 'Send to Nina'}
                </button>
                {canHumanize ? (
                  <button
                    type="button"
                    className="px-3 py-2 rounded text-white bg-cyan-700"
                    onClick={() => humanizeHubWithKryten(hub)}
                    disabled={krytenHumanizingHubId === hub.areaId || ninaSendingHubId === hub.areaId}
                  >
                    {krytenHumanizingHubId === hub.areaId ? 'Humanizing...' : 'Humanize'}
                  </button>
                ) : null}
                <button
                  type="button"
                  className={`px-3 py-2 rounded text-white ${canPublish ? 'bg-emerald-700' : 'bg-gray-500 cursor-not-allowed'}`}
                  onClick={() => publishHubNina(hub, 'update')}
                  disabled={!canPublish || ninaPublishingHubId === hub.areaId}
                >
                  {ninaPublishingHubId === hub.areaId ? 'Publishing...' : 'Update Existing Page (Merge)'}
                </button>
                <button
                  type="button"
                  className={`px-3 py-2 rounded text-white ${canPublish ? 'bg-amber-700' : 'bg-gray-500 cursor-not-allowed'}`}
                  onClick={() => publishHubNina(hub, 'scratch')}
                  disabled={!canPublish || ninaPublishingHubId === hub.areaId}
                >
                  {ninaPublishingHubId === hub.areaId ? 'Publishing...' : 'Republish From Scratch (Recommended)'}
                </button>
                {hub.plaqueId ? (
                  <button
                    type="button"
                    className="px-3 py-2 rounded text-white bg-gray-700"
                    onClick={() => openPlaqueEditor(hub)}
                  >
                    Edit Plaque
                  </button>
                ) : null}
                {plaquePath ? (
                  <a
                    href={plaquePath}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-2 rounded text-white bg-blue-700"
                  >
                    Open Landing Page
                  </a>
                ) : null}
              </div>
              <div className="text-xs text-gray-300">
                <strong>Update Existing Page (Merge)</strong> keeps the same plaque and merges regenerated sections into it, preserving manual placements such as images and ads.
              </div>
              <div className="text-xs text-gray-300">
                <strong>Republish From Scratch</strong> rewrites the plaque body from the latest Nina/Kryten output and is the safer option after rebuilding hubs, uploading more tours, or changing include/exclude rules.
              </div>
              <div className="text-sm font-medium mt-2 text-gray-200">Tour Overrides</div>
              <div className="text-xs text-gray-300">
                `Auto` uses classifier logic, `Include` forces this tour in, `Exclude` removes it from this hub, `Other Activities` adds it to the manual GetYourGuide widget, and `Move To...` reassigns it into another enabled hub.
              </div>
              <div className="text-xs text-gray-400">
                Recommended workflow: leave most on Auto, set 3-5 strongest tours to Include, and mark duplicates/off-topic tours as Exclude before sending to Nina.
              </div>
              <div className="rounded border border-gray-800 bg-gray-950/50 p-3 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    className="border p-2 flex-1 min-w-[220px]"
                    placeholder="Search tour titles..."
                    value={overrideMeta.query}
                    onChange={(e) => updateTourOverrideFilter(hub.areaId, { query: e.target.value })}
                  />
                  <select
                    className="border p-2 min-w-[180px]"
                    value={overrideMeta.location}
                    onChange={(e) => updateTourOverrideFilter(hub.areaId, { location: e.target.value })}
                  >
                    <option value="">All title locations</option>
                    {overrideMeta.locationOptions.map((option) => (
                      <option key={`${hub.areaId}-location-${option.value}`} value={option.value}>
                        {option.label} ({option.count})
                      </option>
                    ))}
                  </select>
                  <select
                    className="border p-2 min-w-[170px]"
                    value={overrideMeta.sortBy}
                    onChange={(e) => updateTourOverrideFilter(hub.areaId, { sortBy: e.target.value })}
                  >
                    {TOUR_OVERRIDE_SORT_OPTIONS.map((option) => (
                      <option key={`${hub.areaId}-sort-${option.value}`} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="px-3 py-2 rounded bg-gray-800 text-white text-xs"
                    onClick={() => updateTourOverrideFilter(hub.areaId, {
                      query: '',
                      location: '',
                      sortBy: 'reviews_desc',
                    })}
                  >
                    Clear Filters
                  </button>
                </div>
                <div className="text-xs text-gray-400">
                  Showing {overrideMeta.filteredTours.length} of {overrideMeta.allTours.length} tours for this hub.
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto space-y-2">
                {overrideMeta.filteredTours.map((tour) => {
                  const mode = hub.overrideByTourId?.[tour.tourId] || 'auto';
                  const autoKey = `${hub.areaId}:${tour.tourId}:clear`;
                  const incKey = `${hub.areaId}:${tour.tourId}:include`;
                  const excKey = `${hub.areaId}:${tour.tourId}:exclude`;
                  const otherKey = `${hub.areaId}:${tour.tourId}:other_activities`;
                  const moving = tourOverrideSavingKey.startsWith(`${hub.areaId}:${tour.tourId}:move:`);
                  return (
                    <div key={`${hub.areaId}-${tour.tourId}`} className="border rounded p-2">
                      <div className="text-sm font-medium">
                        {tour.tourTitle || tour.tourId}
                        {tour.isNew && (
                          <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-blue-700 text-white">
                            NEW
                          </span>
                        )}
                        {tour.isDeal && tour.dealActive && (
                          <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-emerald-700 text-white">
                            DEAL {tour.saveUpTo ? `${Math.round(Number(tour.saveUpTo) * 100)}% OFF` : ''}
                          </span>
                        )}
                        {tour.isDeal && !tour.dealActive && (
                          <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-gray-600 text-white">
                            DEAL EXPIRED
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600">
                        {tour.tourId} · reviews {tour.reviewCount ?? 0} · rating {tour.avgRating ?? 'n/a'}
                        {tour.activityUrl ? (
                          <>
                            {' · '}
                            <a
                              href={withPartnerId(tour.activityUrl)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-700 underline"
                            >
                              Open on GYG
                            </a>
                          </>
                        ) : null}
                      </div>
                      <div className="flex gap-2 mt-1">
                        <button
                          type="button"
                          className={`px-2 py-1 rounded text-xs border ${mode === 'auto' ? 'bg-gray-800 text-white border-gray-700' : 'bg-gray-100 text-gray-900 border-gray-300'}`}
                          disabled={tourOverrideSavingKey === autoKey}
                          onClick={() => setTourOverride(hub, tour, 'clear')}
                        >
                          Auto
                        </button>
                        <button
                          type="button"
                          className={`px-2 py-1 rounded text-xs border ${mode === 'include' ? 'bg-green-700 text-white border-green-800' : 'bg-gray-100 text-gray-900 border-gray-300'}`}
                          disabled={tourOverrideSavingKey === incKey}
                          onClick={() => setTourOverride(hub, tour, 'include')}
                        >
                          Include
                        </button>
                        <button
                          type="button"
                          className={`px-2 py-1 rounded text-xs border ${mode === 'exclude' ? 'bg-red-700 text-white border-red-800' : 'bg-gray-100 text-gray-900 border-gray-300'}`}
                          disabled={tourOverrideSavingKey === excKey}
                          onClick={() => setTourOverride(hub, tour, 'exclude')}
                        >
                          Exclude
                        </button>
                        <button
                          type="button"
                          className={`px-2 py-1 rounded text-xs border ${mode === 'other_activities' ? 'bg-amber-700 text-white border-amber-800' : 'bg-gray-100 text-gray-900 border-gray-300'}`}
                          disabled={tourOverrideSavingKey === otherKey}
                          onClick={() => setTourOverride(hub, tour, 'other_activities')}
                        >
                          Other Activities
                        </button>
                        <select
                          defaultValue=""
                          className="px-2 py-1 rounded text-xs border bg-gray-100 text-gray-900 border-gray-300"
                          disabled={moving || moveTargets.length === 0}
                          onChange={(e) => {
                            const nextTargetAreaId = Number.parseInt(String(e.target.value || ''), 10) || null;
                            if (!nextTargetAreaId) return;
                            setTourOverride(hub, tour, { mode: 'move', targetAreaId: nextTargetAreaId });
                            e.target.value = '';
                          }}
                        >
                          <option value="">{moving ? 'Moving...' : 'Move To...'}</option>
                          {moveTargets.map((targetHub) => (
                            <option key={`${hub.areaId}-${tour.tourId}-move-${targetHub.areaId}`} value={targetHub.areaId}>
                              {targetHub.name} ({targetHub.slug})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
                {overrideMeta.filteredTours.length === 0 && (
                  <div className="text-xs text-gray-400 border rounded p-3">
                    No tours match the current filters.
                  </div>
                )}
              </div>
              <div className="border rounded p-2 bg-black/20">
                <button
                  type="button"
                  className="w-full flex items-center justify-between text-xs font-medium text-gray-200"
                  onClick={() => toggleHubPanel(hub.areaId, 'widget-config')}
                >
                  <span>Auto Widget Config</span>
                  <span>{isHubPanelOpen(hub.areaId, 'widget-config') ? 'Hide' : 'Show'}</span>
                </button>
                {isHubPanelOpen(hub.areaId, 'widget-config') && (
                  <div className="mt-2">
                    <div className="text-xs text-gray-400">
                      Used during publish to create a new GYG widget or update an existing matched one.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                      <input
                        type="text"
                        className="border p-2 w-full"
                        value={widgetDraft.query}
                        onChange={(e) =>
                          setHubWidgetDrafts((prev) => ({
                            ...prev,
                            [key]: { ...widgetDraft, query: e.target.value },
                          }))
                        }
                        placeholder="Search query (e.g. queenstown boat tours)"
                      />
                      <input
                        type="text"
                        className="border p-2 w-full"
                        value={widgetDraft.localeCode}
                        onChange={(e) =>
                          setHubWidgetDrafts((prev) => ({
                            ...prev,
                            [key]: { ...widgetDraft, localeCode: e.target.value },
                          }))
                        }
                        placeholder="Locale (e.g. en-US)"
                      />
                      <input
                        type="number"
                        min="1"
                        max="50"
                        className="border p-2 w-full"
                        value={widgetDraft.numberOfItems}
                        onChange={(e) =>
                          setHubWidgetDrafts((prev) => ({
                            ...prev,
                            [key]: { ...widgetDraft, numberOfItems: e.target.value },
                          }))
                        }
                        placeholder="Number of items"
                      />
                      <input
                        type="text"
                        className="border p-2 w-full"
                        value={widgetDraft.campaignId}
                        onChange={(e) =>
                          setHubWidgetDrafts((prev) => ({
                            ...prev,
                            [key]: { ...widgetDraft, campaignId: e.target.value },
                          }))
                        }
                        placeholder="Campaign ID"
                      />
                      <input
                        type="text"
                        className="border p-2 w-full md:col-span-2"
                        value={widgetDraft.partnerId}
                        onChange={(e) =>
                          setHubWidgetDrafts((prev) => ({
                            ...prev,
                            [key]: { ...widgetDraft, partnerId: e.target.value },
                          }))
                        }
                        placeholder="Partner ID"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-300 mt-2">
                      <input
                        type="checkbox"
                        checked={Boolean(widgetDraft.updateExisting)}
                        onChange={(e) =>
                          setHubWidgetDrafts((prev) => ({
                            ...prev,
                            [key]: { ...widgetDraft, updateExisting: e.target.checked },
                          }))
                        }
                      />
                      Update existing matched widget with this config on publish
                    </label>
                  </div>
                )}
              </div>
                </>
              )}
            </div>
          );
        })}
        {!hubsLoading && disabledHubs.length > 0 && (
          <div className="space-y-2 pt-4">
            <h4 className="font-semibold text-gray-200">Disabled Activity Hubs</h4>
            {disabledHubs.map((hub) => (
              <div key={`disabled-${hub.areaId}`} className="border rounded p-3 bg-black/10 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-100">{hub.name || 'Untitled Hub'}</div>
                  <div className="text-xs text-gray-300 mt-1">
                    Slug: <span className="font-mono text-gray-200">{hub.slug || 'n/a'}</span>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-200">
                  <span>{hubToggleSavingId === hub.areaId ? 'Saving...' : 'Enabled'}</span>
                  <input
                    type="checkbox"
                    checked={false}
                    disabled={hubToggleSavingId === hub.areaId}
                    onChange={(e) => setHubEnabled(hub, e.target.checked)}
                  />
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
